/*!
 * Oneskin Product — vanilla JS controller for the product page (PDP), quick view,
 * and featured-product section.
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'product.js' | asset_url }}" defer="defer"></script>
 *
 * Consolidates, preserving every existing data-attribute/class hook so no Liquid
 * markup changes were required for this pass:
 *   frontend/scripts/lib/product/product.js
 *   frontend/scripts/lib/product-form.js
 *   frontend/scripts/lib/product/*.js  (media manager, variant handler, subscription,
 *     cart handler, sticky ATC bar, quantity input, option buttons, information popup,
 *     inventory counter, variant availability, sibling products, gift card recipient,
 *     featured products block, media helpers, etc.)
 *   frontend/scripts/templates/product.js (mobile description truncate)
 *
 * npm/Vite dependencies dropped and reimplemented natively below:
 *   @shopify/theme-currency, @shopify/theme-product, @shopify/theme-product-form,
 *   slide-anim, evx, @fluorescent/dom, lerp, provide-resize-observer
 *
 * Intentionally simplified in this pass (functional parity, not 1:1 visual/animation parity):
 *   - Product image lightbox is a native modal in this file (assets/product-lightbox.css),
 *     replacing PhotoSwipe: open, zoom, prev/next, counter, keyboard/swipe.
 *   - Info popups use a small self-contained modal instead of the shared evx-based
 *     modal.js bus (that bus still powers other, out-of-scope sitewide components).
 *   - The mobile "featured products" swiper uses a native scroll-snap carousel
 *     instead of Swiper.
 */
;(() => {
  'use strict'

  const theme = window.theme || {}
  const strings = theme.strings || {}
  const productStrings = strings.products || {}
  const cartStrings = strings.cart || {}
  const useCustomEvents = () => Boolean(window.flu && window.flu.states && window.flu.states.useCustomEvents)

  /* -------------------------------------------------------------------------
   * Small utilities (formerly npm / local helper packages)
   * ---------------------------------------------------------------------- */

  function dispatchGlobalEvent(name, data) {
    if (!useCustomEvents()) return
    document.dispatchEvent(new CustomEvent(name, { detail: data || {} }))
  }

  function getMediaQuery(size) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(`--media-${size}`)
    return value || false
  }

  function watchBreakpoint(breakpoint, callback) {
    let isUnder = window.innerWidth <= breakpoint
    const handler = () => {
      const nowUnder = window.innerWidth <= breakpoint
      if (nowUnder !== isUnder) {
        isUnder = nowUnder
        callback()
      }
    }
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }

  function lerp(start, end, amt) {
    return (1 - amt) * start + amt * end
  }

  function shouldAnimate(node) {
    return node.classList.contains('animation') && !document.documentElement.classList.contains('prefers-reduced-motion')
  }

  function delayOffset(node, selectors, offsetStart = 0) {
    let offset = offsetStart
    selectors.forEach((selector) => {
      node.querySelectorAll(selector).forEach((item) => {
        item.style.setProperty('--delay-offset-multiplier', offset)
        offset++
      })
    })
  }

  function intersectionReveal(node, instant = false) {
    const margin = window.matchMedia(getMediaQuery('above-720')).matches ? 200 : 100
    const threshold = instant ? 0 : Math.min(margin / (node.offsetHeight || 1), 0.5)
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add('is-visible')
          observer.disconnect()
        }
      },
      { threshold }
    )
    observer.observe(node)
    return { destroy: () => observer.disconnect() }
  }

  function wrapIframes(elements = []) {
    elements.forEach((el) => {
      const wrapper = document.createElement('div')
      wrapper.classList.add('rte__iframe')
      el.parentNode.insertBefore(wrapper, el)
      wrapper.appendChild(el)
      el.src = el.src
    })
  }

  function wrapTables(elements = []) {
    elements.forEach((el) => {
      const wrapper = document.createElement('div')
      wrapper.classList.add('rte__table-wrapper')
      wrapper.tabIndex = 0
      el.parentNode.insertBefore(wrapper, el)
      wrapper.appendChild(el)
    })
  }

  function makeRequest(url) {
    return fetch(url, { credentials: 'include' }).then((res) => res.text())
  }

  const STORAGE_PREFIX = 'fluco_'
  function localStorageAvailable() {
    try {
      localStorage.setItem('__test__', '__test__')
      localStorage.removeItem('__test__')
      return true
    } catch (e) {
      return false
    }
  }
  function getStorage(key) {
    if (!localStorageAvailable()) return null
    try {
      return JSON.parse(localStorage.getItem(STORAGE_PREFIX + key))
    } catch (e) {
      return null
    }
  }
  function setStorage(key, val) {
    if (!localStorageAvailable()) return null
    localStorage.setItem(STORAGE_PREFIX + key, val)
    return true
  }

  /* -------------------------------------------------------------------------
   * Money / variant helpers (formerly @shopify/theme-currency, @shopify/theme-product,
   * @shopify/theme-product-form)
   * ---------------------------------------------------------------------- */

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '')
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
    const formatString = format || (theme.moneyFormat || '${{amount}}')

    function withDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return 0
      number = (number / 100).toFixed(precision)
      const parts = number.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`)
      const centsPart = parts[1] ? decimal + parts[1] : ''
      return dollars + centsPart
    }

    let value = ''
    const match = formatString.match(placeholderRegex)
    switch (match && match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2)
        break
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0)
        break
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',')
        break
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',')
        break
    }
    return formatString.replace(placeholderRegex, value)
  }

  function formatMoneyWithoutTrailingZeros(val) {
    const moneyFormat = theme.moneyFormat || '${{amount}}'
    const cents = typeof val === 'string' ? Number(val.replace('.', '')) : Number(val)
    if (!Number.isFinite(cents)) return formatMoney(0, moneyFormat)
    if (cents % 100 === 0) {
      return formatMoney(cents, moneyFormat.replace('{{amount}}', '{{amount_no_decimals}}'))
    }
    return formatMoney(cents, moneyFormat)
  }

  function getUrlWithVariant(url, id) {
    if (/variant=/.test(url)) return url.replace(/(variant=)[^&]+/, `$1${id}`)
    if (/\?/.test(url)) return `${url}&variant=${id}`
    return `${url}?variant=${id}`
  }

  function getVariantFromId(product, value) {
    if (!product || typeof value !== 'number') return null
    return product.variants.find((variant) => variant.id === value) || null
  }

  function switchImage(container, imageId, inYourSpaceButton) {
    if (!container) return
    // Quick view uses a stacked gallery — never hide sibling media items.
    if (container.closest('[data-is-quick-view]')) return

    const newImage = container.querySelector(`[data-product-media-wrapper][data-media-item-id="${imageId}"]`)
    const otherImages = Array.from(
      container.querySelectorAll(`[data-product-media-wrapper]:not([data-media-item-id="${imageId}"])`)
    )

    if (newImage) {
      newImage.classList.remove('hidden')
      if (inYourSpaceButton && newImage.dataset.mediaType === 'model') {
        inYourSpaceButton.setAttribute('data-shopify-model3d-id', newImage.dataset.mediaItemId)
      }
    }
    otherImages.forEach((image) => image.classList.add('hidden'))
  }

  /* -------------------------------------------------------------------------
   * Image-variant filename matching (frontend/scripts/lib/product/image-variant-matcher.js)
   * ---------------------------------------------------------------------- */

  function normalizeVariantOption(option) {
    if (!option) return ''
    return option
      .toLowerCase()
      .replace(/[\/ -]/g, '_')
      .replace(/_+/g, '_')
  }

  function filenameMatchesVariant(filename, variantNormalized) {
    if (!filename || !variantNormalized) return false
    if (filename.includes('allvariants')) return true
    if (filename === variantNormalized) return true
    return (
      filename.includes(`_${variantNormalized}_`) ||
      filename.startsWith(`${variantNormalized}_`) ||
      filename.endsWith(`_${variantNormalized}`)
    )
  }

  function filenameMatchesAnyVariant(filename, allVariantOptions) {
    if (!filename || !allVariantOptions?.length) return false
    return allVariantOptions.some((opt) => filenameMatchesVariant(filename, normalizeVariantOption(opt)))
  }

  function shouldShowImageForVariant(filename, variantNormalized, allVariantOptions) {
    if (!filename) return true
    const normalizedFilename = filename.toLowerCase()
    if (normalizedFilename.includes('allvariants')) return true

    if (filenameMatchesVariant(normalizedFilename, variantNormalized)) {
      const hasLongerMatch = allVariantOptions.some((opt) => {
        const otherNormalized = normalizeVariantOption(opt)
        if (otherNormalized === variantNormalized) return false
        if (!otherNormalized.includes(variantNormalized)) return false
        return filenameMatchesVariant(normalizedFilename, otherNormalized)
      })
      return !hasLongerMatch
    }

    if (!filenameMatchesAnyVariant(normalizedFilename, allVariantOptions)) return true
    return false
  }

  function applyVariantMediaFilter({ mediaElements, variantTitle, allVariantTitles }) {
    if (!variantTitle || !mediaElements?.length) return
    const variantNormalized = normalizeVariantOption(variantTitle)

    mediaElements.forEach((imageEl) => {
      const filename = imageEl.dataset?.imageVariant?.toLowerCase()
      if (!filename) return
      const shouldShow = shouldShowImageForVariant(filename, variantNormalized, allVariantTitles)
      imageEl.setAttribute('data-image-visible', shouldShow ? 'true' : 'false')
    })
  }

  function resolveVariantFeaturedMediaId(product, variant) {
    if (!variant) return null
    if (variant.featured_media?.id != null) return variant.featured_media.id

    const imageId = variant.featured_image?.id
    if (imageId == null) return null

    const mediaItems = product?.media
    if (Array.isArray(mediaItems) && mediaItems.length) {
      const byMediaId = mediaItems.find((item) => item.id?.toString() === imageId.toString())
      if (byMediaId?.id != null) return byMediaId.id
      const byPreviewId = mediaItems.find((item) => item.preview_image?.id?.toString() === imageId.toString())
      if (byPreviewId?.id != null) return byPreviewId.id
    }
    return imageId
  }

  function repositionMediaBadges(container, { featuredMediaId } = {}) {
    const mobileContainer = container.querySelector('.product__media-container.below-mobile')
    const desktopContainer = container.querySelector('.product__media-container.above-mobile')

    function findFeaturedVisibleSlide(mediaContainer) {
      if (featuredMediaId != null) {
        const featured = mediaContainer.querySelector(
          `[data-product-media-wrapper][data-media-item-id="${featuredMediaId}"][data-image-visible="true"]`
        )
        if (featured) return featured
      }
      return mediaContainer.querySelector('[data-product-media-wrapper][data-image-visible="true"]')
    }

    function moveBadgeToFirstVisible(mediaContainer) {
      const target = findFeaturedVisibleSlide(mediaContainer)
      const badge = mediaContainer.querySelector('[data-product-media-badges]')
      if (!badge || !target || badge.parentElement === target) return
      target.appendChild(badge)
    }

    if (mobileContainer) moveBadgeToFirstVisible(mobileContainer)
    if (desktopContainer) moveBadgeToFirstVisible(desktopContainer)
  }

  /* -------------------------------------------------------------------------
   * Product lightbox — native modal (assets/product-lightbox.css)
   * Open / zoom / prev-next / counter / keyboard + swipe. No PhotoSwipe.
   * ---------------------------------------------------------------------- */

  const LIGHTBOX_ZOOM = 2.5
  let sharedLightboxUi = null

  function lightboxIcon(name, fallback) {
    return (theme.icons && theme.icons[name]) || fallback
  }

  function sourceFromLightboxLink(el) {
    if (!el) return ''
    const img = el.querySelector('img')
    return (
      el.getAttribute('data-pswp-src') ||
      el.getAttribute('href') ||
      (img && (img.currentSrc || img.src)) ||
      ''
    )
  }

  function ensureLightboxUi() {
    if (sharedLightboxUi) return sharedLightboxUi

    const root = document.createElement('div')
    root.className = 'product-lightbox'
    root.setAttribute('data-product-lightbox', '')
    root.setAttribute('hidden', '')
    root.setAttribute('role', 'dialog')
    root.setAttribute('aria-modal', 'true')
    root.setAttribute('aria-label', 'Product image gallery')
    root.innerHTML = `
      <div class="product-lightbox__counter" data-lightbox-counter aria-live="polite"></div>
      <div class="product-lightbox__actions">
        <button type="button" class="product-lightbox__btn product-lightbox__zoom" data-lightbox-zoom aria-label="Zoom image">
          ${lightboxIcon('zoom', '+')}
        </button>
        <button type="button" class="product-lightbox__btn product-lightbox__close" data-lightbox-close aria-label="Close">
          ${lightboxIcon('close', '&times;')}
        </button>
      </div>
      <button type="button" class="product-lightbox__prev" data-lightbox-prev aria-label="Previous image">
        ${lightboxIcon('chevron', '&#8249;')}
      </button>
      <button type="button" class="product-lightbox__next" data-lightbox-next aria-label="Next image">
        ${lightboxIcon('chevron', '&#8250;')}
      </button>
      <div class="product-lightbox__stage" data-lightbox-stage>
        <img class="product-lightbox__image" data-lightbox-image alt="" />
      </div>
    `
    document.body.appendChild(root)

    const img = root.querySelector('[data-lightbox-image]')
    const stage = root.querySelector('[data-lightbox-stage]')
    const counter = root.querySelector('[data-lightbox-counter]')
    const state = {
      items: [],
      index: 0,
      zoomed: false,
      scale: 1,
      x: 0,
      y: 0,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      originX: 0,
      originY: 0,
      touchStartX: 0,
      touchStartY: 0,
    }

    function applyTransform() {
      img.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`
    }

    function setZoomed(zoomed) {
      state.zoomed = zoomed
      state.scale = zoomed ? LIGHTBOX_ZOOM : 1
      if (!zoomed) {
        state.x = 0
        state.y = 0
      }
      root.classList.toggle('is-zoomed', zoomed)
      stage.classList.toggle('is-panning', false)
      applyTransform()
    }

    function show(index) {
      if (!state.items.length) return
      state.index = ((index % state.items.length) + state.items.length) % state.items.length
      const item = state.items[state.index]
      img.src = item.src
      img.alt = item.alt || ''
      counter.textContent = `${state.index + 1} / ${state.items.length}`
      root.classList.toggle('is-single', state.items.length <= 1)
      setZoomed(false)
    }

    function open(items, index) {
      state.items = items
      root.hidden = false
      root.classList.add('is-open')
      document.documentElement.classList.add('overflow-hidden')
      show(index)
      root.querySelector('[data-lightbox-close]')?.focus({ preventScroll: true })
    }

    function close() {
      root.classList.remove('is-open')
      root.hidden = true
      document.documentElement.classList.remove('overflow-hidden')
      setZoomed(false)
      img.removeAttribute('src')
      state.items = []
    }

    function isOpen() {
      return root.classList.contains('is-open')
    }

    root.querySelector('[data-lightbox-close]').addEventListener('click', close)
    root.querySelector('[data-lightbox-prev]').addEventListener('click', () => show(state.index - 1))
    root.querySelector('[data-lightbox-next]').addEventListener('click', () => show(state.index + 1))
    root.querySelector('[data-lightbox-zoom]').addEventListener('click', () => setZoomed(!state.zoomed))

    stage.addEventListener('click', (e) => {
      // Click empty stage (not the image) closes when not zoomed
      if (!state.zoomed && e.target === stage) close()
    })

    stage.addEventListener('dblclick', (e) => {
      e.preventDefault()
      setZoomed(!state.zoomed)
    })

    stage.addEventListener('pointerdown', (e) => {
      if (!state.zoomed || e.button !== 0) return
      state.dragging = true
      state.dragStartX = e.clientX
      state.dragStartY = e.clientY
      state.originX = state.x
      state.originY = state.y
      stage.classList.add('is-panning')
      stage.setPointerCapture?.(e.pointerId)
    })

    stage.addEventListener('pointermove', (e) => {
      if (!state.dragging) return
      state.x = state.originX + (e.clientX - state.dragStartX)
      state.y = state.originY + (e.clientY - state.dragStartY)
      applyTransform()
    })

    const endDrag = (e) => {
      if (!state.dragging) return
      state.dragging = false
      stage.classList.remove('is-panning')
      if (e?.pointerId != null) stage.releasePointerCapture?.(e.pointerId)
    }
    stage.addEventListener('pointerup', endDrag)
    stage.addEventListener('pointercancel', endDrag)

    stage.addEventListener(
      'touchstart',
      (e) => {
        if (state.zoomed || e.touches.length !== 1) return
        state.touchStartX = e.touches[0].clientX
        state.touchStartY = e.touches[0].clientY
      },
      { passive: true }
    )

    stage.addEventListener(
      'touchend',
      (e) => {
        if (state.zoomed || !e.changedTouches.length) return
        const dx = e.changedTouches[0].clientX - state.touchStartX
        const dy = e.changedTouches[0].clientY - state.touchStartY
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return
        show(state.index + (dx < 0 ? 1 : -1))
      },
      { passive: true }
    )

    document.addEventListener('keydown', (e) => {
      if (!isOpen()) return
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowLeft') show(state.index - 1)
      if (e.key === 'ArrowRight') show(state.index + 1)
    })

    sharedLightboxUi = { open, close, isOpen, root }
    return sharedLightboxUi
  }

  function collectGalleryItems(gallery) {
    return Array.from(gallery.querySelectorAll('.lightbox-image'))
      .map((el) => {
        const img = el.querySelector('img')
        return {
          el,
          src: sourceFromLightboxLink(el),
          alt: img?.getAttribute('alt') || '',
        }
      })
      .filter((item) => item.src)
  }

  function initProductLightbox(container) {
    const triggers = Array.from(container.querySelectorAll('.lightbox-image'))
    if (!triggers.length) return { unload() {} }

    const ui = ensureLightboxUi()
    const handlers = []

    triggers.forEach((trigger) => {
      const onClick = (e) => {
        e.preventDefault()
        const gallery =
          trigger.closest('.lightbox-media-container') ||
          trigger.closest('.product__media-container') ||
          container
        const items = collectGalleryItems(gallery)
        if (!items.length) return
        const index = Math.max(
          0,
          items.findIndex((item) => item.el === trigger)
        )
        ui.open(items, index)
      }
      trigger.addEventListener('click', onClick)
      handlers.push(() => trigger.removeEventListener('click', onClick))
    })

    return {
      unload() {
        handlers.forEach((off) => off())
        if (ui.isOpen()) ui.close()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Media component: native video controls + Shopify model-viewer/XR + YouTube
   * (frontend/scripts/lib/media.js) — uses Shopify's own global APIs, no Vite dep.
   * ---------------------------------------------------------------------- */

  function initVideoNative(container) {
    // Detached Tailwind control is bound by assets/video-pause-control.js
    if (container.querySelector('[data-video-pause]')) return () => {}

    const pause = container.querySelector('.video-pause')
    const video = container.querySelector('video')
    if (!pause || !video) return () => {}

    const handleClick = (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (video.paused) {
        const p = video.play()
        if (p !== undefined) p.catch(() => {})
        pause.setAttribute('data-status', 'play')
      } else {
        video.pause()
        pause.setAttribute('data-status', 'pause')
      }
    }
    const stopTouch = (e) => e.stopPropagation()
    pause.addEventListener('click', handleClick)
    pause.addEventListener('touchstart', stopTouch, { passive: true })
    pause.addEventListener('touchend', stopTouch, { passive: true })
    return () => {
      pause.removeEventListener('click', handleClick)
      pause.removeEventListener('touchstart', stopTouch)
      pause.removeEventListener('touchend', stopTouch)
    }
  }

  function initMedia(node) {
    if (!node) return { pauseActiveMedia() {} }
    const { Shopify, YT } = window
    const elements = Array.from(node.querySelectorAll('[data-interactive]'))
    if (!elements.length) return { pauseActiveMedia() {} }

    const acceptedTypes = ['video', 'model', 'external_video']
    let activeMedia = null
    let featuresLoaded = false
    const instances = {}

    const isSwiperContainer = node.classList.contains('swiper')

    if (isSwiperContainer) {
      const handleVideoTap = (e) => {
        // Detached Tailwind control is bound by assets/video-pause-control.js
        if (e.target.closest?.('[data-video-pause]')) return

        const pause = e.target.closest?.('.video-pause')
        if (!pause) return
        const media = pause.closest?.('.media')
        const video = media?.querySelector('video')
        if (!video) return

        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'touchstart') return

        if (video.paused) {
          const p = video.play()
          if (p !== undefined) p.catch(() => {})
          pause.setAttribute('data-status', 'play')
        } else {
          video.pause()
          pause.setAttribute('data-status', 'pause')
        }
      }
      node.addEventListener('touchstart', handleVideoTap, { capture: true })
      node.addEventListener('touchend', handleVideoTap, { capture: true })
      node.addEventListener('click', handleVideoTap, { capture: true })
    }

    elements.forEach((el) => {
      if (el.dataset.mediaType === 'video' && !isSwiperContainer) initVideoNative(el)
    })

    function pauseActiveMedia(instance) {
      if (!activeMedia || instance === activeMedia) return
      if (activeMedia.viewer) {
        activeMedia.container.classList.remove('model-active')
        activeMedia.viewer.pause()
      }
      activeMedia = null
    }

    function playModel(instance) {
      pauseActiveMedia(instance)
      instance.viewer.play()
      instance.container.classList.add('model-active')
      activeMedia = instance
      setTimeout(() => {
        instance.container.querySelector('model-viewer')?.focus()
      }, 300)
    }

    function initElement(el) {
      const { mediaId, mediaType } = el.dataset
      if (!mediaType || !acceptedTypes.includes(mediaType)) return
      if (Object.keys(instances).includes(mediaId)) return

      const instance = { id: mediaId, type: mediaType, container: el, media: el.querySelector('video') || el.children[0] }
      instances[mediaId] = instance

      if (instance.type === 'model' && Shopify?.ModelViewerUI) {
        instance.viewer = new Shopify.ModelViewerUI(el.querySelector('model-viewer'))
        const modelPoster = el.querySelector('.model-poster')
        if (modelPoster) {
          modelPoster.addEventListener('click', (e) => {
            e.preventDefault()
            playModel(instance)
          })
        }
      }
    }

    function runInit() {
      if (featuresLoaded) elements.forEach(initElement)
    }

    if (!Shopify || !Shopify.loadFeatures) {
      if ('YT' in window && Boolean(YT?.loaded)) {
        elements.forEach(initElement)
      } else {
        window.onYouTubeIframeAPIReady = function () {
          elements.forEach(initElement)
        }
      }
      return { pauseActiveMedia }
    }

    try {
      Shopify.loadFeatures(
        [
          { name: 'model-viewer-ui', version: '1.0' },
          { name: 'shopify-xr', version: '1.0' },
        ],
        () => {
          featuresLoaded = true
          if ('YT' in window && Boolean(YT?.loaded)) {
            runInit()
          } else {
            window.onYouTubeIframeAPIReady = runInit
          }
        }
      )
    } catch (err) {
      // model-viewer/xr failed to load, non-critical
    }

    return { pauseActiveMedia }
  }

  /* -------------------------------------------------------------------------
   * Scroll container (desktop thumbnail rail) (frontend/scripts/lib/scroll-container.js)
   * ---------------------------------------------------------------------- */

  function scrollContainer(node) {
    const sentinals = Array.from(node.querySelectorAll('.scroll-sentinal'))
    const buttons = Array.from(node.querySelectorAll('.scroll-button'))
    const { axis, startAtEnd } = node.dataset
    const scrollAttribute = axis === 'vertical' ? 'scrollTop' : 'scrollLeft'
    const scrollerViewport = node.querySelector('[data-scroll-container-viewport]')

    const onLoad = () => {
      node.classList.add('scroll-container-initialized')
      if (startAtEnd === 'true' && scrollerViewport) {
        const scrollDirection = axis === 'vertical' ? 'scrollHeight' : 'scrollWidth'
        scrollerViewport[scrollAttribute] = scrollerViewport[scrollDirection] * 2
        node.dataset.startAtEnd = 'false'
      }
    }
    window.addEventListener('load', onLoad, { once: true })

    const buttonHandlers = buttons.map((button) => {
      const handler = () => {
        const scrollOffset = 100
        if (!scrollerViewport) return
        if (button.dataset.position === 'start') {
          scrollerViewport[scrollAttribute] =
            scrollerViewport[scrollAttribute] < scrollOffset * 1.5 ? 0 : scrollerViewport[scrollAttribute] - scrollOffset
        } else {
          scrollerViewport[scrollAttribute] += scrollOffset
        }
      }
      button.addEventListener('click', handler)
      return () => button.removeEventListener('click', handler)
    })

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const position = entry.target.dataset.position
          node.setAttribute(`data-at-${position}`, entry.isIntersecting ? 'true' : 'false')
        })
      },
      { root: scrollerViewport }
    )
    sentinals.forEach((sentinal) => intersectionObserver.observe(sentinal))

    function scrollTo(element) {
      if (!scrollerViewport) return
      const scrollDistance =
        axis === 'vertical'
          ? element.offsetTop - element.getBoundingClientRect().height
          : element.offsetLeft - element.getBoundingClientRect().width
      scrollerViewport[scrollAttribute] = scrollDistance
    }

    return {
      scrollTo,
      unload() {
        sentinals.forEach((sentinal) => intersectionObserver.unobserve(sentinal))
        buttonHandlers.forEach((off) => off())
        window.removeEventListener('load', onLoad)
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Variant context (parses embedded ProductJson) — frontend/scripts/lib/product/variant-context.js
   * ---------------------------------------------------------------------- */

  function createVariantContext(container) {
    let cachedProduct = null

    function getProduct() {
      if (cachedProduct) return cachedProduct
      const jsonEl = container.querySelector('[id^="ProductJson-"]')
      if (!jsonEl) return null
      try {
        cachedProduct = JSON.parse(jsonEl.textContent)
      } catch {
        cachedProduct = null
      }
      return cachedProduct
    }

    function invalidate() {
      cachedProduct = null
    }

    function getAllVariantTitles() {
      return getProduct()?.variants?.map((variant) => variant.title) || []
    }

    function findVariant(predicate) {
      return getProduct()?.variants?.find(predicate) || null
    }

    function getUrlVariantId() {
      return new URLSearchParams(window.location.search).get('variant')
    }

    function getVariantFromContext(detail = {}) {
      const { variantTitle, variantId, featuredMediaId } = typeof detail === 'string' ? { variantTitle: detail } : detail
      const urlVariantId = getUrlVariantId()
      const variant = variantId
        ? findVariant((v) => v.id.toString() === variantId.toString())
        : variantTitle
          ? findVariant((v) => v.title === variantTitle)
          : urlVariantId
            ? findVariant((v) => v.id.toString() === urlVariantId.toString())
            : null

      if (variant) {
        return {
          title: variant.title,
          variantId: variant.id,
          featuredMediaId: resolveVariantFeaturedMediaId(getProduct(), variant) ?? featuredMediaId ?? null,
        }
      }

      return {
        title: variantTitle || getAllVariantTitles()[0] || null,
        variantId: variantId ?? null,
        featuredMediaId: featuredMediaId ?? null,
      }
    }

    return { getVariantFromContext, getAllVariantTitles, getUrlVariantId, invalidate }
  }

  /* -------------------------------------------------------------------------
   * Desktop media switcher — frontend/scripts/lib/product/desktop-media-switcher.js
   * ---------------------------------------------------------------------- */

  function createDesktopMediaSwitcher({
    container,
    isFullProduct,
    isFeaturedProduct,
    enableMultipleVariantMedia,
    viewInYourSpace,
    initialMediaId,
    isMobileCarouselActive,
  }) {
    let previousMediaId = initialMediaId

    function switchCurrentImage(id) {
      container.querySelectorAll('.product__media').forEach((imagesWrap) => switchImage(imagesWrap, id, viewInYourSpace))
    }

    function updateForVariant(variant) {
      if (!variant.featured_media) return { shouldRefresh: false }
      const mediaId = variant.featured_media.id

      if (enableMultipleVariantMedia === 'true' && mediaId !== previousMediaId) {
        previousMediaId = mediaId
        return { shouldRefresh: true, variantId: variant.id }
      }

      const noop = { shouldRefresh: false }

      // Quick view uses a stacked gallery — keep all media visible.
      if (!isFullProduct && container.dataset.isQuickView !== 'true') {
        switchCurrentImage(mediaId)
        return noop
      }

      if (container.dataset.isQuickView === 'true') {
        return noop
      }

      if (isMobileCarouselActive()) return noop

      if (isFeaturedProduct) {
        switchCurrentImage(mediaId)
        return noop
      }

      return noop
    }

    return { switchCurrentImage, updateForVariant }
  }

  /* -------------------------------------------------------------------------
   * Media manager coordinator — frontend/scripts/lib/product/product-media-manager.js
   * ---------------------------------------------------------------------- */

  function createMediaManager(config) {
    const { container, isFullProduct, isFeaturedProduct, enableMultipleVariantMedia, viewInYourSpace } = config

    let desktopMedia = null
    let productThumbnails = null
    let productThumbnailItems = []
    let productThumbnailsScroller = null
    let events = []
    let mediaInstance = null
    let mobileMediaInstance = null
    let lightboxInstance = null
    let slideChangeCleanup = null

    const variantContext = createVariantContext(container)

    const desktopSwitcher = createDesktopMediaSwitcher({
      container,
      isFullProduct,
      isFeaturedProduct,
      enableMultipleVariantMedia,
      viewInYourSpace,
      initialMediaId: config.initialMediaId,
      isMobileCarouselActive,
    })

    function isMobileCarouselActive() {
      const slider = container.querySelector('[data-product-mobile-aero-slider]')
      return !!slider?.aeroSlider
    }

    function notifyMobileCarouselUpdate(ctx) {
      container.dispatchEvent(new CustomEvent('product:mobile-carousel-update', { detail: ctx }))
    }

    function destroyMobileCarousel() {
      container.dispatchEvent(new CustomEvent('product:mobile-carousel-destroy'))
    }

    function syncVariantMediaFilters(detail = {}) {
      const ctx = variantContext.getVariantFromContext(detail)
      if (!ctx.title) return null

      applyVariantMediaFilter({
        mediaElements: container.querySelectorAll('[data-image-variant]'),
        variantTitle: ctx.title,
        allVariantTitles: variantContext.getAllVariantTitles(),
      })
      repositionMediaBadges(container, { featuredMediaId: ctx.featuredMediaId })

      return ctx
    }

    function updateVariantMedia(detail = {}) {
      const ctx = syncVariantMediaFilters(detail)
      if (ctx) notifyMobileCarouselUpdate(ctx)
    }

    function reinitMobileCarouselAfterRefresh(detail = {}) {
      destroyMobileCarousel()
      variantContext.invalidate()
      updateVariantMedia(detail)
    }

    function initThumbnails() {
      productThumbnails = container.querySelector('[data-product-thumbnails]')
      productThumbnailItems = Array.from(container.querySelectorAll('[data-product-thumbnail]'))
      if (productThumbnails) productThumbnailsScroller = scrollContainer(productThumbnails)
    }

    function loadMedia() {
      const aeroGalleryEl = container.querySelector('[data-product-aero-gallery]')
      const mobileMediaEl = aeroGalleryEl || container.querySelector('.product__media-container.below-mobile')
      desktopMedia = container.querySelector('.product__media-container.above-mobile')
      mediaInstance = initMedia(desktopMedia)
      mobileMediaInstance = initMedia(mobileMediaEl)
      initThumbnails()
      lightboxInstance = initProductLightbox(container)

      const onSlideChange = () => mobileMediaInstance?.pauseActiveMedia()
      container.addEventListener('product:mobile-carousel-slide-change', onSlideChange)
      slideChangeCleanup = () => container.removeEventListener('product:mobile-carousel-slide-change', onSlideChange)
    }

    function unloadMedia() {
      slideChangeCleanup?.()
      slideChangeCleanup = null
      lightboxInstance?.unload()
      destroyMobileCarousel()
      productThumbnailsScroller?.unload()
    }

    async function refreshOverviewWithVariant(variantId, { reloadEvents }) {
      const requestURL = `${window.location.pathname}?section_id=${config.sectionId}&variant=${variantId}`
      const target = container.querySelector('.product__primary-left')
      if (!target) return

      const mediaContainersEls = Array.from(
        target.querySelectorAll(
          '.product__media-container.above-mobile, .product__media-container.below-mobile, [data-product-aero-gallery]'
        )
      )
      mediaContainersEls.forEach((el) => el.classList.add('loading'))

      try {
        const text = await makeRequest(requestURL)
        const source = new DOMParser().parseFromString(text, 'text/html').querySelector('.product__primary-left')
        if (!source) return

        target.innerHTML = source.innerHTML
        variantContext.invalidate()

        unloadMedia()
        events.forEach((unsubscribe) => unsubscribe())

        loadMedia()
        const urlVariantId = variantContext.getUrlVariantId()
        const refreshedCtx = variantContext.getVariantFromContext({ variantId: urlVariantId })
        repositionMediaBadges(container, { featuredMediaId: refreshedCtx?.featuredMediaId })
        reloadEvents()

        if (urlVariantId) reinitMobileCarouselAfterRefresh({ variantId: urlVariantId })
      } catch (err) {
        console.error('[product] Failed to refresh variant overview', err)
      }
    }

    function highlightActiveThumbnail(photos, variant) {
      const thumb = photos.querySelector(`[data-thumbnail-id="${variant.featured_media.id}"]`)
      productThumbnailItems.forEach((t) => t.classList.remove('active'))
      if (thumb) thumb.classList.add('active')
    }

    function scrollThumbnails(variant) {
      if (!variant.featured_media || !productThumbnails) return
      const groupThumb = productThumbnails
        .querySelector(`[data-thumbnail-id="${variant.featured_media.id}"]`)
        ?.closest('li')
      if (groupThumb && productThumbnailsScroller) productThumbnailsScroller.scrollTo(groupThumb)
    }

    function setEvents(ev) {
      events = ev
    }

    return {
      initThumbnails,
      loadMedia,
      unloadMedia,
      destroyMobileCarousel,
      syncVariantMediaFilters,
      updateMobileCarouselOnly: notifyMobileCarouselUpdate,
      refreshOverviewWithVariant,
      updateForVariant: desktopSwitcher.updateForVariant,
      highlightActiveThumbnail,
      scrollThumbnails,
      setEvents,
      get desktopMedia() {
        return desktopMedia
      },
      get productThumbnails() {
        return productThumbnails
      },
      get productThumbnailItems() {
        return productThumbnailItems
      },
    }
  }

  /* -------------------------------------------------------------------------
   * update-prices / update-sku / update-buy-button / unit-pricing
   * ---------------------------------------------------------------------- */

  function updatePrices(container, variant, productTemplate) {
    const priceEls = Array.from(container.querySelectorAll('[data-price]'))
    const comparePriceEls = Array.from(container.querySelectorAll('[data-compare-price]'))
    const unavailableString = productStrings.product?.unavailable || ''

    if (!variant) {
      priceEls.forEach((el) => (el.innerHTML = unavailableString))
      comparePriceEls.forEach((el) => (el.innerHTML = ''))
      return
    }

    const defaultProdContainer = container.querySelector('.product__top')
    const quickProdContainer = document.querySelector('.quick-product')
    const productContainer = productTemplate ? defaultProdContainer : quickProdContainer
    if (!productContainer) return

    const { zeroPriceDisplay, zeroPriceCustomContent, soldOutPriceDisplay, soldOutPriceCustomContent } =
      productContainer.dataset

    let priceContentType = 'price'
    let priceContent = formatMoney(variant.price)

    if (variant.available) {
      if (variant.compare_at_price === null && variant.price === 0) {
        if (zeroPriceDisplay === 'hide') {
          priceContentType = 'hide'
          priceContent = ''
        } else if (zeroPriceDisplay === 'replace') {
          priceContentType = 'custom'
          priceContent = zeroPriceCustomContent
        }
      }
    } else if (soldOutPriceDisplay === 'hide') {
      priceContentType = 'hide'
      priceContent = ''
    } else if (soldOutPriceDisplay === 'replace') {
      priceContentType = 'custom'
      priceContent = soldOutPriceCustomContent
    }

    container.querySelectorAll('[data-price-display-type]').forEach((el) => {
      el.setAttribute('data-price-display-type', priceContentType)
    })
    container.querySelectorAll('[data-discount-display]').forEach((el) => {
      el.classList.toggle('invisible', priceContentType !== 'price')
    })

    priceEls.forEach((el) => (el.innerHTML = priceContent))
    comparePriceEls.forEach(
      (el) =>
        (el.innerHTML =
          variant.compare_at_price > variant.price && priceContentType === 'price'
            ? formatMoney(variant.compare_at_price)
            : '')
    )
  }

  function updateSku(container, variant) {
    const skuElement = container.querySelector('[data-product-sku]')
    const skuContainer = container.querySelector('.product__vendor_and_sku')
    if (!skuElement) return

    const skuLabel = productStrings.product?.sku || 'SKU'

    if (!variant || !variant.sku) {
      skuElement.innerText = ''
      skuContainer?.setAttribute('data-showing-sku', 'false')
      return
    }

    skuElement.innerText = `${skuLabel}: ${variant.sku}`
    skuContainer?.setAttribute('data-showing-sku', 'true')
  }

  function getBaseUnit(unitPriceMeasurement) {
    return unitPriceMeasurement.reference_value === 1
      ? unitPriceMeasurement.reference_unit
      : unitPriceMeasurement.reference_value + unitPriceMeasurement.reference_unit
  }

  function updateUnitPrices(container, variant) {
    const unitPriceContainers = Array.from(container.querySelectorAll('[data-unit-price-container]'))
    const unitPrices = Array.from(container.querySelectorAll('[data-unit-price]'))
    const unitPriceBases = Array.from(container.querySelectorAll('[data-unit-base]'))
    const showUnitPricing = !variant || !variant.unit_price

    unitPriceContainers.forEach((el) => el.classList.toggle('unit-price--available', !showUnitPricing))
    if (!variant || !variant.unit_price) return

    unitPrices.forEach((el) => (el.innerText = formatMoney(variant.unit_price)))
    unitPriceBases.forEach((el) => (el.innerText = getBaseUnit(variant.unit_price_measurement)))
  }

  function formatCleanMoney(price) {
    const moneyFormat = theme.moneyFormat || '${{amount}}'
    const roundedPrice = Math.ceil(price / 100) * 100
    const noDecimalsFormat = moneyFormat.replace('{{amount}}', '{{amount_no_decimals}}')
    return formatMoney(roundedPrice, noDecimalsFormat)
  }

  function updateAriaLabel(btn, price, state = 'available') {
    if (!btn || !btn.dataset) return
    const { ariaTemplateAvailable, ariaTemplateUnavailable, ariaTemplateSoldOut, productTitle } = btn.dataset
    let ariaLabel = ''

    if (state === 'available' && ariaTemplateAvailable) {
      ariaLabel = ariaTemplateAvailable.replace('{price}', price || '')
    } else if (state === 'unavailable' && ariaTemplateUnavailable) {
      ariaLabel = ariaTemplateUnavailable
    } else if (state === 'sold-out' && ariaTemplateSoldOut) {
      ariaLabel = ariaTemplateSoldOut
    } else if (productTitle) {
      ariaLabel = state === 'available' ? `Add ${productTitle} to cart, ${price}` : `${productTitle} - ${state}`
    }

    if (ariaLabel) btn.setAttribute('aria-label', ariaLabel)
  }

  function renderAtcPriceLabel(text, langAvailable, includePrice, actualPrice, compareAtPrice, regularPriceLabel) {
    const formattedPrice = formatCleanMoney(actualPrice)
    const hasCompareAt = includePrice && compareAtPrice && compareAtPrice > actualPrice
    const formattedCompare = hasCompareAt ? formatCleanMoney(compareAtPrice) : ''

    if (text) {
      if (!includePrice) {
        text.textContent = langAvailable
      } else if (hasCompareAt) {
        text.innerHTML = `${langAvailable} – <s class="atc-compare-price">${formattedCompare}</s> <span class="atc-final-price">${formattedPrice}</span>`
      } else {
        text.textContent = `${langAvailable} – ${formattedPrice}`
      }
    }

    if (hasCompareAt) {
      const compareLabel = regularPriceLabel || 'Regular price'
      return `${formattedPrice}, ${compareLabel} ${formattedCompare}`
    }

    return formattedPrice
  }

  function updateBuyButton(btn, variant, subscriptionInfo = null) {
    const text = btn.querySelector('[data-add-to-cart-text]')
    const { langAvailable, langUnavailable, langSoldOut, showPriceInAtc, regularPriceLabel } = btn.dataset
    const includePrice = showPriceInAtc === 'true'
    btn.classList.remove('loading')

    if (!variant) {
      btn.setAttribute('disabled', 'disabled')
      if (text) text.textContent = langUnavailable
      updateAriaLabel(btn, null, 'unavailable')
    } else if (variant.available) {
      btn.removeAttribute('disabled')

      if (subscriptionInfo) {
        const { isSubscription, oneTimePrice, subscriptionPrice, compareAtPrice } = subscriptionInfo
        const displayPrice = includePrice
          ? oneTimePrice
          : isSubscription && subscriptionPrice
            ? subscriptionPrice
            : oneTimePrice
        const ariaPrice = renderAtcPriceLabel(
          text,
          langAvailable,
          includePrice,
          displayPrice,
          compareAtPrice,
          regularPriceLabel
        )
        updateAriaLabel(btn, ariaPrice, 'available')
      } else {
        const ariaPrice = renderAtcPriceLabel(
          text,
          langAvailable,
          includePrice,
          variant.price,
          variant.compare_at_price,
          regularPriceLabel
        )
        updateAriaLabel(btn, ariaPrice, 'available')
      }
    } else {
      btn.setAttribute('disabled', 'disabled')
      if (text) text.textContent = langSoldOut
      updateAriaLabel(btn, null, 'sold-out')
    }
  }

  /* -------------------------------------------------------------------------
   * Variant handler — frontend/scripts/lib/product/product-variant-handler.js
   * ---------------------------------------------------------------------- */

  function createVariantHandler({ container, formElement, isFullProduct }) {
    const cachedElements = {
      buyButtons: Array.from(container.querySelectorAll('[data-add-to-cart]')),
      priceWrapper: container.querySelector('.product__price'),
      displayedDiscount: container.querySelector('[data-discount-display]'),
    }

    function updateOptionLabel(srcElement) {
      const optionParentWrapper = srcElement.closest('.product__option')
      const optionLabel = optionParentWrapper && optionParentWrapper.querySelector('[data-selected-value-for-option]')
      if (optionLabel) optionLabel.textContent = srcElement.value
    }

    function updatePriceDisplay(variant) {
      if (!cachedElements.priceWrapper) return
      cachedElements.priceWrapper.classList.toggle('hide', !variant)
    }

    function updateDiscountDisplay(variant) {
      if (!cachedElements.displayedDiscount) return
      const newDiscountEl = variant && container.querySelector(`[variant-discount-display][variant-id="${variant.id}"]`)
      cachedElements.displayedDiscount.textContent = variant && newDiscountEl ? newDiscountEl.textContent : ''
    }

    function updateBuyButtons(variant, priceInfo = null) {
      cachedElements.buyButtons.forEach((buyButton) => updateBuyButton(buyButton, variant, priceInfo))
    }

    function updateVariantUrl(variant) {
      const url = getUrlWithVariant(window.location.href, variant.id)
      window.history.replaceState({ path: url }, '', url)
    }

    function updateVariantSelect(variant) {
      const selectedVariantOpt = container.querySelector(`[data-variant-select] [value='${variant.id}']`)
      if (selectedVariantOpt) selectedVariantOpt.selected = true
      formElement?.dispatchEvent(new Event('change'))
    }

    function handleUnavailableVariant() {
      cachedElements.buyButtons.forEach((button) => updateBuyButton(button, false))
    }

    function dispatchVariantChangeEvent(variant) {
      dispatchGlobalEvent('product:variant-change', { variant })
    }

    return {
      updateOptionLabel,
      updatePriceDisplay,
      updateDiscountDisplay,
      updateBuyButtons,
      updateVariantUrl,
      updateVariantSelect,
      handleUnavailableVariant,
      dispatchVariantChangeEvent,
      get cachedElements() {
        return cachedElements
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Subscription handler — frontend/scripts/lib/product/product-subscription.js
   * ---------------------------------------------------------------------- */

  function createSubscriptionHandler({ container, formElement, productData }) {
    const subscriptionInputs = Array.from(container.querySelectorAll('[name="product-subscription"]'))
    const subscriptionSelect = container.querySelector('[data-product-subscription-select]')
    if (!subscriptionInputs.length || !subscriptionSelect) return null

    const selectEl = subscriptionSelect.querySelector('select')
    const productSubscriptionId = formElement.querySelector('[data-product-subscription-id]')

    function isSubscriptionSelected() {
      const checked = container.querySelector('input[name="product-subscription"]:checked')
      return checked?.value === 'subscription'
    }

    function getCurrentVariant() {
      const variantInput = formElement.querySelector('[name="id"]')
      const variantId = variantInput?.value
      if (variantId && productData?.variants) {
        return productData.variants.find((v) => v.id.toString() === variantId) || null
      }
      return null
    }

    function getSubscriptionPrice(variant) {
      if (!variant?.selling_plan_allocations?.length) return null
      const selectedPlanId = productSubscriptionId?.value
      if (!selectedPlanId) return variant.selling_plan_allocations[0]?.price || null
      const allocation = variant.selling_plan_allocations.find(
        (a) => a.selling_plan_id.toString() === selectedPlanId.toString()
      )
      return allocation?.price || null
    }

    function updateBuyButtonWithSubscription() {
      const buyButtonEls = Array.from(container.querySelectorAll('[data-add-to-cart]'))
      const currentVariant = getCurrentVariant()
      if (!currentVariant) return

      const subscriptionInfo = {
        isSubscription: isSubscriptionSelected(),
        oneTimePrice: currentVariant.price,
        subscriptionPrice: getSubscriptionPrice(currentVariant),
        compareAtPrice: currentVariant.compare_at_price,
      }
      buyButtonEls.forEach((buyButton) => updateBuyButton(buyButton, currentVariant, subscriptionInfo))
    }

    function removeButtonLoadingState() {
      Array.from(container.querySelectorAll('[data-add-to-cart]')).forEach((button) => button.classList.remove('loading'))
    }

    function initSubscriptionListeners() {
      const events = []
      if (!selectEl || !productSubscriptionId) return events

      const potentialPointsEl = container.querySelector('.order-placed-potential-points-amount')

      subscriptionInputs.forEach((input) => {
        if (input.value === 'subscription' && input.checked) {
          if (input.dataset.sellingPlanId) selectEl.value = input.dataset.sellingPlanId
          productSubscriptionId.setAttribute('name', 'selling_plan')
          productSubscriptionId.value = selectEl.value
          const potentialPointsAmount = input?.dataset?.pricePotentialPoints
          if (potentialPointsEl && potentialPointsAmount) potentialPointsEl.innerText = potentialPointsAmount
        }
      })

      updateBuyButtonWithSubscription()
      removeButtonLoadingState()

      const subscriptionChangeHandler = (e) => {
        const value = e?.target?.value
        const sellingPlanId = e?.target?.dataset?.sellingPlanId

        productSubscriptionId.removeAttribute('name')

        if (value === 'one-time') {
          selectEl.value = 'one-time'
          productSubscriptionId.value = ''
        } else {
          if (sellingPlanId) selectEl.value = sellingPlanId
          productSubscriptionId.value = selectEl.value
          if (value === 'subscription') productSubscriptionId.setAttribute('name', 'selling_plan')
        }

        const potentialPointsAmount = e?.target?.dataset?.pricePotentialPoints
        if (potentialPointsEl && potentialPointsAmount) potentialPointsEl.innerText = potentialPointsAmount

        updateBuyButtonWithSubscription()
      }

      subscriptionInputs.forEach((input) => {
        input.addEventListener('change', subscriptionChangeHandler)
        events.push(() => input.removeEventListener('change', subscriptionChangeHandler))
      })

      const selectChangeHandler = (e) => {
        const value = e?.target?.value
        let matchingInput = null

        if (!value || value === 'one-time') {
          matchingInput = container.querySelector('input[name="product-subscription"][value="one-time"]')
        } else {
          matchingInput = container.querySelector(
            `input[name="product-subscription"][data-selling-plan-id="${value}"]`
          )
        }

        if (matchingInput) {
          matchingInput.checked = true
          matchingInput.dispatchEvent(new Event('change', { bubbles: true }))
          return
        }

        productSubscriptionId.value = value || ''
        if (value && value !== 'one-time') {
          productSubscriptionId.setAttribute('name', 'selling_plan')
        } else {
          productSubscriptionId.removeAttribute('name')
        }
        updateBuyButtonWithSubscription()
      }
      selectEl.addEventListener('change', selectChangeHandler)
      events.push(() => selectEl.removeEventListener('change', selectChangeHandler))

      return events
    }

    return {
      isSubscriptionSelected,
      getSubscriptionPrice,
      getCurrentVariant,
      updateBuyButtonWithSubscription,
      removeButtonLoadingState,
      initSubscriptionListeners,
      get hasSubscription() {
        return subscriptionInputs.length > 0 && !!subscriptionSelect
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Cart handler — frontend/scripts/glow/cart.js + product-cart-handler.js +
   * product-error-handler.js
   * ---------------------------------------------------------------------- */

  const cartRoutes = (theme.routes && theme.routes.cart) || {}
  const cartPaths = {
    base: `${cartRoutes.base || '/cart'}.js`,
    add: `${cartRoutes.add || '/cart/add'}.js`,
  }

  function cartGet() {
    return fetch(cartPaths.base, { method: 'GET', credentials: 'include' }).then((res) => res.json())
  }

  async function cartAddItem(form) {
    const formData = new FormData(form)
    const mainItem = {
      id: parseInt(formData.get('id')),
      quantity: parseInt(formData.get('quantity') || 1),
    }

    const sellingPlan = formData.get('selling_plan')
    if (sellingPlan) mainItem.selling_plan = parseInt(sellingPlan)

    const items = [mainItem]

    if (window.theme?.settings?.enable_gift_auto_add && window.freeGifts && sellingPlan) {
      const mainVariantId = mainItem.id
      const matchingFreeGift = window.freeGifts.find((freeGift) =>
        freeGift.trigger_variants.some((variant) => variant.variant_id === mainVariantId)
      )

      if (matchingFreeGift) {
        try {
          const currentCart = await cartGet()
          const giftAlreadyInCart = currentCart.items?.some(
            (item) =>
              item.variant_id === matchingFreeGift.gift_variant.variant_id &&
              item.properties?._NSHC_GIFT_FOR === mainVariantId.toString()
          )
          if (!giftAlreadyInCart) {
            items.push({ id: matchingFreeGift.gift_variant.variant_id, quantity: 1, properties: { _NSHC_GIFT: true } })
          }
        } catch (error) {
          items.push({ id: matchingFreeGift.gift_variant.variant_id, quantity: 1, properties: { _NSHC_GIFT: true } })
        }
      }
    }

    if (window.oneskin && window.oneskin.events) {
      const intervalEl =
        form.querySelector('[name="product-subscription"]:checked') ||
        form.querySelector('.product-subscription__select') ||
        form.querySelector('[data-product-subscription-select] select')
      let interval
      if (intervalEl) {
        interval = intervalEl.value
        if (intervalEl.options && intervalEl.selectedIndex >= 0) {
          interval = intervalEl.options[intervalEl.selectedIndex].text || intervalEl.value
        }
        if (intervalEl.value === 'one-time') interval = 'one_time'
      }
      window.oneskin.events.prepare('add_to_cart', {
        source_location: form.closest('[data-is-quick-view]') ? 'quick_view' : 'pdp',
        is_subscription: !!sellingPlan,
        subscription_interval: interval ? String(interval).replace(/\s+/g, '_').toLowerCase() : undefined,
        is_bundle: form.dataset.isBundle === 'true',
        bundle_size: form.dataset.bundleSize || undefined,
        product_name: form.dataset.productTitle,
        product_variant: form.dataset.productVariant,
        product_category: form.dataset.productCategory,
        item_id: String(mainItem.id),
      })
    }

    const res = await fetch(cartPaths.add, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items }),
    }).then((r) => r.json())

    if (res.status == '422') {
      const error = { code: 422, message: res.description }
      dispatchGlobalEvent('cart:error', { errorMessage: error.message })
      throw error
    }

    const cart = await cartGet()
    const order = getStorage('cart_order') || []
    const newOrder = [res.variant_id, ...order.filter((i) => i !== res.variant_id)]
    setStorage('cart_order', JSON.stringify(newOrder))

    dispatchGlobalEvent('cart:updated', { cart })
    return { item: res, cart }
  }

  function handleCartError(error, quantityErrorEl, container) {
    if (error && error.message) {
      if (typeof error.message === 'object') {
        const giftCardContainer = container.querySelector('.product-form__gift-card-recipient')
        if (giftCardContainer) {
          const sectionID = giftCardContainer.dataset.sectionId
          Object.entries(error.message).forEach(([key, value]) => {
            const errorMessageID = `display-gift-card-recipient-${key}-error--${sectionID}`
            const errorMessage = container.querySelector(`#${errorMessageID}`)
            const errorInput = container.querySelector(`#display-gift-card-recipient-${key}--${sectionID}`)
            if (errorMessage) {
              errorMessage.innerText = value
              errorMessage.classList.remove('hidden')
            }
            if (errorInput) {
              errorInput.setAttribute('aria-invalid', true)
              errorInput.setAttribute('aria-describedby', errorMessageID)
            }
          })
        }
      } else if (quantityErrorEl) {
        quantityErrorEl.innerText = error.message
        quantityErrorEl.classList.remove('hidden')
      }
    } else if (quantityErrorEl) {
      quantityErrorEl.innerText = quantityErrorEl.getAttribute('data-fallback-error-message') || 'An error occurred'
      quantityErrorEl.classList.remove('hidden')
    }
    console.error('[product] cart add failed', error)
  }

  function createCartHandler({ container, formElement, quantityError, getCurrentVariant }) {
    function setButtonsLoading(isLoading) {
      Array.from(container.querySelectorAll('[data-add-to-cart]')).forEach((button) =>
        button.classList.toggle('loading', isLoading)
      )
    }

    function handleAddToCartSuccess(item) {
      setButtonsLoading(false)
      if (window.NSHybridCart) window.NSHybridCart.open()
      dispatchGlobalEvent('cart:item-added', { product: item })
    }

    function handleAddToCartError(error) {
      handleCartError(error, quantityError, container)
      setButtonsLoading(false)
    }

    function handleFormSubmit(event) {
      event.preventDefault()
      const targetForm = event.detail?.form || event.target
      const backInStockForm = targetForm?.closest?.('[data-back-in-stock-form-element]')
      if (backInStockForm) return

      const currentVariant = getCurrentVariant()
      if (!currentVariant || !currentVariant.available) return
      if (!formElement) return

      setButtonsLoading(true)
      if (quantityError) quantityError.classList.add('hidden')

      cartAddItem(formElement)
        .then(({ item }) => handleAddToCartSuccess(item))
        .catch((error) => handleAddToCartError(error))
    }

    return { handleFormSubmit, handleAddToCartSuccess, handleAddToCartError, setButtonsLoading }
  }

  /* -------------------------------------------------------------------------
   * ProductForm — frontend/scripts/lib/product-form.js
   * ---------------------------------------------------------------------- */

  function ProductFormController(container, form, product) {
    const $$nestedVariants = container.querySelectorAll('[data-nested-variant]')
    const $$productImageVariant = container.querySelectorAll('[data-image-variant]')
    const $productMediaDesktop = container.querySelector('.js-product-media-desktop')
    const $hiddenSellingPlan = container.querySelector('[data-product-subscription-select]')
    const $sellingPlanInput = container.querySelector('[data-product-subscription-id]')
    const $variantSelect = container.querySelector('.product-form:not(.product-otp-form) #variant-selector')

    const listeners = []
    let dermOption = 'With dermaroller'
    let optionInputs = []
    let formQuantityInput = []
    let propertyInputs = []

    function handleVariantSelection(event, isNested = false) {
      const target = event.target
      const { optionId, nestedFreqPriceTrigger } = target.dataset

      if (isNested) {
        if (!target.hasAttribute('data-nested-variant')) return
        dermOption = target.value
        syncNestedSelectionAcrossFrequencies(target.value)
      }

      let resolvedVariantId = optionId
      if (!resolvedVariantId) {
        const $radioContainer = target.closest('.product__form-radio-container')
        const $nestedOption = $radioContainer?.querySelector('[data-nested-variant][data-option-id]:checked')
        resolvedVariantId = $nestedOption?.dataset.optionId
      }
      if (!resolvedVariantId) return

      if (!isNested) handleFreqPricingSelector(resolvedVariantId, nestedFreqPriceTrigger)
      handleImageVariantsByVariantId(resolvedVariantId)
    }

    function dispatchVariantMediaUpdate(variant) {
      if (!variant) return
      document.dispatchEvent(
        new CustomEvent('variant:media-update', {
          detail: {
            variantTitle: variant.title,
            variantId: variant.id,
            featuredMediaId: resolveVariantFeaturedMediaId(product, variant),
            state: 'product-form',
          },
        })
      )
    }

    function handleImageVariantsByVariantId(variantId) {
      if (!variantId) return
      dispatchVariantMediaUpdate(product.variants.find((v) => v.id.toString() === variantId.toString()))
    }

    function handleImageVariants(variantTitle) {
      if (!variantTitle) return
      dispatchVariantMediaUpdate(product.variants.find((v) => v.title === variantTitle))
    }

    function getVariantIdFromUrlOrDefault() {
      const variantId = new URLSearchParams(window.location.search).get('variant')
      const defaultVariantId = container.querySelector('[data-current-variant-id]')?.dataset.currentVariantId
      return { variantId: variantId || defaultVariantId, isFromUrl: !!variantId }
    }

    function selectVariantFromURL() {
      const { variantId } = getVariantIdFromUrlOrDefault()
      if (!variantId) return

      const matchingVariant = product.variants.find((v) => v.id.toString() === variantId)
      if (!matchingVariant) return

      const optionsMap = new Map(
        matchingVariant.options.map((value, index) => {
          const opt = product.options[index]
          const optionName = opt?.name ?? opt
          return [optionName, normalizeValue(value)]
        })
      )
      if (matchingVariant.option2) dermOption = matchingVariant.option2
      updateFormInputs(optionsMap, matchingVariant)

      const formStateAfter = getFormState()
      updateSellingPlan(formStateAfter)
      setIdInputValue(variantId)

      requestAnimationFrame(() => handleImageVariantsByVariantId(variantId))
    }

    function onFormEvent(eventType) {
      return () => {
        const formState = getFormState()
        const $$supplyFreq = document.querySelectorAll('input.top-level_input')
        if ($$supplyFreq.length > 0) {
          $$supplyFreq.forEach((input) => toggleNestedContainerVisibility(input))
          updateSellingPlanFromFormState(formState)
        }

        if (eventType === 'product-form:option-change' && formState.variant?.title) {
          handleImageVariants(formState.variant.title)
        }

        dispatchFormEvent(eventType, { target: event.target }, formState)
      }
    }

    function onSubmit(event) {
      event.preventDefault()
      const formState = getFormState()
      if (formState.variant?.requires_selling_plan) updateSellingPlan(formState)
      setIdInputValue(formState.variant.id)
      dispatchFormEvent('product-form:submit', event, formState)
    }

    function getFormState() {
      return { options: getOptions(), variant: getVariant(), properties: getProperties(), quantity: getQuantity() }
    }

    function getVariantFromOptions(collection) {
      if (!product?.variants || !product?.options) return null
      if (!Array.isArray(collection) || collection.length === 0) return product.variants[0] || null
      return findMatchingVariant(buildOptionArray(collection))
    }

    function getVariant() {
      return getVariantFromOptions(getOptions())
    }

    function initProductImageVariant() {
      if (!$$productImageVariant?.length) return
      const { variantId } = getVariantIdFromUrlOrDefault()

      if (!variantId) {
        $$productImageVariant.forEach((imageEl) => imageEl.setAttribute('data-image-visible', 'true'))
        dispatchVariantMediaUpdate(product.variants[0])
        return
      }
      handleImageVariantsByVariantId(variantId)
    }

    function toggleNestedContainerVisibility(input) {
      const $parentContainer = input.closest('.product__form-radio-container')
      const $nestedContainer = $parentContainer?.querySelector('.nested-container')
      if (!$nestedContainer) return

      const $$nestedRadios = Array.from($nestedContainer.querySelectorAll('.input-radio .input-radio__input'))
      const isChecked = input.checked

      $nestedContainer.classList.toggle('hidden', !isChecked)
      $$nestedRadios.forEach((radio) => {
        radio.disabled = !isChecked
        if (isChecked && !radio.checked && radio.value === dermOption) radio.click()
      })
    }

    function updateSellingPlanFromFormState(formState) {
      const { variant } = formState
      if (variant?.selling_plan_allocations?.length > 0) {
        updateSellingPlan(formState)
        return
      }

      const firstVariant = product.variants?.[0]
      if (firstVariant?.selling_plan_allocations?.length > 0 && firstVariant.option2 && formState.options?.length > 1) {
        const fallbackFormState = {
          ...formState,
          variant: firstVariant,
          options: formState.options.map((opt, index) => (index === 1 ? { ...opt, value: firstVariant.option2 } : opt)),
        }
        updateSellingPlan(fallbackFormState)
      }
    }

    function updateFormInputs(optionsMap, matchingVariant) {
      optionsMap.forEach((value, optionName) => {
        const $input = Array.from(container.querySelectorAll(`input[name="options[${optionName}]"]`)).find(
          (input) => input.value.trim().toLowerCase() === value
        )

        if ($input) {
          $input.checked = true
          $input.dispatchEvent(new Event('change', { bubbles: true }))
          if ($input.classList.contains('top-level_input')) updateNestedInputs($input, matchingVariant)
        } else {
          const $select = container.querySelector(`select[name="options[${optionName}]"]`)
          if ($select) {
            const $optionButtonsWrapper = $select.closest('[data-option-buttons]')
            const $matchingButton =
              $optionButtonsWrapper &&
              Array.from($optionButtonsWrapper.querySelectorAll('[data-button]')).find(
                (btn) => btn.dataset.optionValue?.trim().toLowerCase() === value
              )
            const $opt = Array.from($select.options).find((o) => o.value.trim().toLowerCase() === value)

            if ($opt) {
              $select.value = $opt.value
              if ($optionButtonsWrapper) {
                $optionButtonsWrapper.querySelectorAll('[data-button]').forEach((btn) => {
                  btn.classList.toggle('selected', btn === $matchingButton)
                })
              }
              $select.dispatchEvent(new Event('change', { bubbles: true }))
            } else if ($matchingButton) {
              $matchingButton.click()
            }
          }
        }
      })
    }

    function handleFreqPricingSelector(optionId, triggerName) {
      if (!optionId || !triggerName) return
      const $freqPricingEl = container.querySelector(`[data-freq-price="${triggerName}"]`)
      if (!$freqPricingEl) return

      const currentVariant = product.variants.find((variant) => variant.id === Number(optionId))
      if (!currentVariant) return

      $freqPricingEl.querySelectorAll('[data-freq-type]').forEach((freqTypeEl) => {
        const { freqType } = freqTypeEl.dataset
        const amount = freqType.includes('price') ? currentVariant.price : currentVariant.compare_at_price
        freqTypeEl.innerText = formatMoneyWithoutTrailingZeros(amount)
      })
    }

    function syncNestedSelectionAcrossFrequencies(selectedNestedValue) {
      const normalizedValue = selectedNestedValue?.trim().toLowerCase()
      if (!normalizedValue) return

      const topLevelInputs = Array.from(container.querySelectorAll('.top-level_input[data-nested-freq-price-trigger]'))
      topLevelInputs.forEach((topLevelInput) => {
        const triggerName = topLevelInput.dataset.nestedFreqPriceTrigger
        if (!triggerName) return

        const cardContainer = topLevelInput.closest('.product__form-radio-container')
        if (!cardContainer) return

        const nestedRadios = Array.from(cardContainer.querySelectorAll('.nested-container .input-radio__input'))
        if (nestedRadios.length === 0) return

        const matchingNestedRadio = nestedRadios.find((radio) => radio.value?.trim().toLowerCase() === normalizedValue)
        if (!matchingNestedRadio) return

        nestedRadios.forEach((radio) => (radio.checked = radio === matchingNestedRadio))
        handleFreqPricingSelector(matchingNestedRadio.dataset.optionId, triggerName)
      })
    }

    function buildOptionArray(collection) {
      const optionArray = []
      collection.forEach((option) => {
        const index = product.options.findIndex((opt) => {
          const productOptionName = opt?.name || opt
          return typeof productOptionName === 'string' && productOptionName.toLowerCase() === option.name.toLowerCase()
        })
        if (index !== -1) optionArray[index] = option.value
      })
      return optionArray
    }

    function findMatchingVariant(optionArray) {
      return (
        product.variants.find((variant) => {
          if (!Array.isArray(variant.options)) return false
          return optionArray.every((optionValue, index) => {
            if (optionValue === undefined) return true
            const variantOptionValue = variant.options[index]
            if (variantOptionValue === undefined) return false
            return String(variantOptionValue).trim().toLowerCase() === String(optionValue).trim().toLowerCase()
          })
        }) || null
      )
    }

    function getOptions() {
      return serializeOptionValues(optionInputs, (item) => {
        if (item.name) {
          const match = /options\[(.*?)\]/.exec(item.name)
          item.name = match ? match[1] : item.name
        }
        return item
      })
    }

    function getProperties() {
      const properties = serializePropertyValues(propertyInputs, (propertyName) => {
        const match = /properties\[(.*?)\]/.exec(propertyName)
        return match ? match[1] : propertyName
      })
      return Object.keys(properties).length === 0 ? null : properties
    }

    function getQuantity() {
      return formQuantityInput[0] ? Number.parseInt(formQuantityInput[0].value, 10) : 1
    }

    function dispatchFormEvent(eventType, originalEvent, formState) {
      container.dispatchEvent(
        new CustomEvent(eventType, {
          detail: { formState, srcElement: originalEvent.target, form },
          bubbles: true,
          cancelable: true,
        })
      )
    }

    function setIdInputValue(value) {
      let $idInput = form.querySelector('[name="id"]')
      if (!$idInput) {
        $idInput = document.createElement('input')
        $idInput.type = 'hidden'
        $idInput.name = 'id'
        form.appendChild($idInput)
      }
      $idInput.value = value.toString()
    }

    function updateSellingPlan(eventData) {
      if (!eventData.variant?.selling_plan_allocations?.length) return
      const sellingPlanId = eventData.variant.selling_plan_allocations[0].selling_plan_id

      if ($hiddenSellingPlan) $hiddenSellingPlan.value = sellingPlanId
      if ($sellingPlanInput) {
        $sellingPlanInput.value = sellingPlanId
        $sellingPlanInput.setAttribute('name', 'selling_plan')
      }
      if ($variantSelect) $variantSelect.value = eventData.variant.id
    }

    function updateNestedInputs(topLevelInput, variant) {
      const $parentContainer = topLevelInput.closest('.product__form-radio-container')
      const $nestedContainer = $parentContainer?.querySelector('.nested-container')
      if (!$nestedContainer) return

      const normalizedNestedValue = variant.options[1]?.trim().toLowerCase()
      if (!normalizedNestedValue) return

      const $matchingInput = Array.from($nestedContainer.querySelectorAll('.input-radio__input')).find(
        (radio) => radio.value.trim().toLowerCase() === normalizedNestedValue
      )
      if ($matchingInput) {
        $matchingInput.checked = true
        dermOption = $matchingInput.value
        handleFreqPricingSelector(variant.id, topLevelInput.dataset.nestedFreqPriceTrigger)
      }
    }

    function initDermOptionSelection() {
      const checkedNestedInput = container.querySelector('.nested-container .input-radio__input:checked')
      if (checkedNestedInput?.value) {
        dermOption = checkedNestedInput.value
        return
      }

      const { variantId } = getVariantIdFromUrlOrDefault()
      const selectedVariant = variantId && product.variants?.find((v) => v.id.toString() === variantId)
      if (selectedVariant?.option2) {
        dermOption = selectedVariant.option2
        return
      }
      if (product.variants?.[0]?.option2) dermOption = product.variants[0].option2
    }

    function setupVariantListeners(elements, isNested) {
      elements.forEach((element) => {
        const handler = (event) => handleVariantSelection(event, isNested)
        element.addEventListener('change', handler)
        listeners.push(() => element.removeEventListener('change', handler))
      })
    }

    function setupInputListeners(selector, eventType) {
      return Array.from(container.querySelectorAll(selector)).map((element) => {
        const handler = onFormEvent(eventType)
        element.addEventListener('change', handler)
        listeners.push(() => element.removeEventListener('change', handler))
        return element
      })
    }

    function destroy() {
      listeners.forEach((unsubscribe) => unsubscribe())
    }

    setupVariantListeners(container.querySelectorAll('.top-level_input'), false)
    setupVariantListeners($$nestedVariants, true)

    initProductImageVariant()

    if ($productMediaDesktop) $productMediaDesktop.classList.remove('loading')

    form.addEventListener('submit', onSubmit)
    listeners.push(() => form.removeEventListener('submit', onSubmit))
    optionInputs = setupInputListeners('[name^="options"]', 'product-form:option-change')
    formQuantityInput = setupInputListeners('[data-quantity-input]', 'product-form:quantity-change')
    propertyInputs = setupInputListeners('[name^="properties"]', 'product-form:property-change')

    if (typeof window.watchKlaviyoOpen === 'function') window.watchKlaviyoOpen()
    if (typeof window.watchKlaviyoClose === 'function') window.watchKlaviyoClose()

    selectVariantFromURL()
    initDermOptionSelection()

    return { getVariant, destroy }
  }

  function serializeOptionValues(inputs, transform) {
    return inputs.reduce((options, input) => {
      if (shouldSerializeInput(input)) options.push(transform({ name: input.name, value: input.value }))
      return options
    }, [])
  }

  function serializePropertyValues(inputs, transform) {
    return inputs.reduce((properties, input) => {
      if (shouldSerializeInput(input)) properties[transform(input.name)] = input.value
      return properties
    }, {})
  }

  function shouldSerializeInput(input) {
    const isIncluded = input.checked || (input.type !== 'radio' && input.type !== 'checkbox')
    if (!isIncluded) return false

    const $nestedContainer = input.closest('.nested-container')
    if ($nestedContainer) {
      const $primaryInput = input.closest('.product__form-radio-container')?.querySelector('.top-level_input')
      return $primaryInput?.checked ?? false
    }
    return true
  }

  function normalizeValue(value) {
    return value?.trim().toLowerCase() || ''
  }

  /* -------------------------------------------------------------------------
   * Quantity input, option buttons, info popup, inventory counter,
   * variant availability, sibling products, gift card recipient, social share
   * — small focused modules (frontend/scripts/lib/product/*.js)
   * ---------------------------------------------------------------------- */

  function initQuantityInput(container) {
    const quantityWrapper = container.querySelector('.quantity-input')
    if (!quantityWrapper) return null

    const quantityInput = quantityWrapper.querySelector('[data-quantity-input]')
    const addQuantity = quantityWrapper.querySelector('[data-add-quantity]')
    const subtractQuantity = quantityWrapper.querySelector('[data-subtract-quantity]')
    if (!quantityInput || !addQuantity || !subtractQuantity) return null

    const handleAdd = () => {
      quantityInput.value = parseInt(quantityInput.value) + 1
      quantityInput.dispatchEvent(new Event('change'))
    }
    const handleSubtract = () => {
      const currentValue = parseInt(quantityInput.value)
      if (currentValue === 1) return
      quantityInput.value = currentValue - 1
      quantityInput.dispatchEvent(new Event('change'))
    }

    addQuantity.addEventListener('click', handleAdd)
    subtractQuantity.addEventListener('click', handleSubtract)

    return {
      unload() {
        addQuantity.removeEventListener('click', handleAdd)
        subtractQuantity.removeEventListener('click', handleSubtract)
      },
    }
  }

  function initOptionButtons(elements) {
    const groups = elements.map((el) => {
      const select = el.querySelector('select')
      const buttons = Array.from(el.querySelectorAll('[data-button]'))

      const clickHandler = (e) => {
        e.preventDefault()
        const buttonEl = e.currentTarget
        const { optionHandle } = buttonEl.dataset
        buttons.forEach((btn) => btn.classList.toggle('selected', btn.dataset.optionHandle === optionHandle))

        const opt = select?.querySelector(`[data-value-handle="${optionHandle}"]`)
        if (!opt || !select) return
        opt.selected = true
        select.dispatchEvent(new Event('change', { bubbles: true }))
      }

      buttons.forEach((button) => button.addEventListener('click', clickHandler))
      return () => buttons.forEach((button) => button.removeEventListener('click', clickHandler))
    })

    return {
      destroy() {
        groups.forEach((off) => off())
      },
    }
  }

  // Self-contained info popup modal — replaces the shared evx-based modal.js bus
  // (which stays in place for other, out-of-scope components).
  let sharedInfoPopupOverlay = null
  function getInfoPopupOverlay() {
    if (sharedInfoPopupOverlay) return sharedInfoPopupOverlay

    const overlay = document.createElement('div')
    overlay.setAttribute('data-product-info-popup-overlay', '')
    overlay.className = 'fixed inset-0 z-[999] hidden items-center justify-center bg-black/60 p-4'
    overlay.innerHTML = `
      <div data-info-popup-wash class="absolute inset-0"></div>
      <div class="modal__inner relative z-10 max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6">
        <button type="button" data-info-popup-close aria-label="${
          theme.strings?.general?.accessibility?.close_modal || 'Close'
        }" class="absolute right-4 top-4 text-2xl leading-none">&times;</button>
        <div data-info-popup-content></div>
      </div>
    `
    document.body.appendChild(overlay)

    const contentSlot = overlay.querySelector('[data-info-popup-content]')
    function close() {
      overlay.classList.add('hidden')
      overlay.classList.remove('flex')
      document.body.setAttribute('data-fluorescent-overlay-open', 'false')
      document.documentElement.classList.remove('overflow-hidden')
      contentSlot.innerHTML = ''
    }
    function open(contentNode) {
      contentSlot.innerHTML = ''
      contentSlot.appendChild(contentNode.cloneNode(true))
      overlay.classList.remove('hidden')
      overlay.classList.add('flex')
      document.body.setAttribute('data-fluorescent-overlay-open', 'true')
      document.documentElement.classList.add('overflow-hidden')
    }

    overlay.querySelector('[data-info-popup-wash]').addEventListener('click', close)
    overlay.querySelector('[data-info-popup-close]').addEventListener('click', close)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !overlay.classList.contains('hidden')) close()
    })

    sharedInfoPopupOverlay = { open, close }
    return sharedInfoPopupOverlay
  }

  function initInformationPopup(node) {
    const popupTriggers = Array.from(node.querySelectorAll('[data-popup-trigger]'))
    if (!popupTriggers.length) return null

    const clickHandler = (e) => {
      e.preventDefault()
      e.stopPropagation()
      const { modalContentId } = e.currentTarget.dataset
      const content = node.querySelector(`#${modalContentId}`)
      if (!content) return
      getInfoPopupOverlay().open(content)
    }

    popupTriggers.forEach((trigger) => trigger.addEventListener('click', clickHandler))

    return {
      unload() {
        popupTriggers.forEach((trigger) => trigger.removeEventListener('click', clickHandler))
      },
    }
  }

  function initInventoryCounter(container, { id, variantsInventories }) {
    const counterContainer = container.querySelector('[data-inventory-counter]')
    if (!counterContainer) return null

    const inventoryMessageElement = container.querySelector('.inventory-counter__message')
    const progressBar = container.querySelector('.inventory-counter__bar-progress')
    const { lowInventoryThreshold, showUntrackedQuantity, stockCountdownMax, unavailableText } = counterContainer.dataset

    if (!lowInventoryThreshold?.match(/^[0-9]+$/) || !stockCountdownMax?.match(/^[0-9]+$/)) return null

    const threshold = parseInt(lowInventoryThreshold, 10)
    const countDownMax = parseInt(stockCountdownMax, 10)

    function productInventoryValid(product) {
      return (
        product?.inventory_message &&
        (product.inventory_management !== null || (product.inventory_management === null && showUntrackedQuantity === 'true'))
      )
    }

    function checkThreshold({ inventory_policy, inventory_quantity, inventory_management }) {
      counterContainer.classList.remove('inventory--low')
      if (inventory_management !== null) {
        if (inventory_quantity <= 0 && inventory_policy === 'deny') {
          counterContainer.classList.add('inventory--empty')
          counterContainer.setAttribute('data-stock-category', 'empty')
        } else if (inventory_quantity <= threshold || (inventory_quantity <= 0 && inventory_policy === 'continue')) {
          counterContainer.setAttribute('data-stock-category', 'low')
        } else {
          counterContainer.setAttribute('data-stock-category', 'sufficient')
        }
      } else if (inventory_management === null && showUntrackedQuantity === 'true') {
        counterContainer.setAttribute('data-stock-category', 'sufficient')
      }
    }

    function setProgressBar(inventoryQuantity, inventoryManagement) {
      if (!progressBar) return
      if (inventoryManagement === null && showUntrackedQuantity === 'true') {
        progressBar.style.width = '100%'
        return
      }
      if (inventoryQuantity <= 0) {
        progressBar.style.width = '0%'
        return
      }
      const progressValue = inventoryQuantity < countDownMax ? (inventoryQuantity / countDownMax) * 100 : 100
      progressBar.style.width = `${progressValue}%`
    }

    function setInventoryMessage(message) {
      if (inventoryMessageElement) inventoryMessageElement.innerText = message
    }

    function applyInventory(inventory) {
      counterContainer.classList.toggle('hidden', !productInventoryValid(inventory))
      checkThreshold(inventory)
      setProgressBar(inventory.inventory_quantity, inventory.inventory_management)
      setInventoryMessage(inventory.inventory_message)
    }

    if (variantsInventories[id]) applyInventory(variantsInventories[id])

    function update(variant) {
      if (!variant) {
        counterContainer.classList.remove('hidden')
        counterContainer.classList.add('inventory--unavailable')
        counterContainer.setAttribute('data-stock-category', 'unavailable')
        setProgressBar(0)
        setInventoryMessage(unavailableText)
        return
      }
      if (variantsInventories[variant.id]) applyInventory(variantsInventories[variant.id])
    }

    return { update }
  }

  function initVariantAvailability(container) {
    const variantsWrapper = container.querySelector('.product__variants-wrapper')
    if (!variantsWrapper) return null

    const { enableDynamicProductOptions, currentVariantId } = variantsWrapper.dataset
    if (enableDynamicProductOptions === 'false') return null

    const variantsJsonEl = container.querySelector('[data-variant-json]')
    if (!variantsJsonEl) return null

    const productVariants = JSON.parse(variantsJsonEl.innerText)
    const variantSelectors = Array.from(container.querySelectorAll('.dynamic-variant-input'))
    const variantSelectorWrappers = Array.from(container.querySelectorAll('.dynamic-variant-input-wrap'))

    function escapeQuotes(str) {
      return str.replace(/"|'/g, (m) => (m === '"' ? '\\"' : "\\'"))
    }

    function createAvailableOptionsTree(variants, currentlySelectedValues) {
      return variants.reduce(
        (options, variant) => {
          Object.keys(options).forEach((index) => {
            if (variant[index] === null) return
            let entry = options[index].find((option) => option.value === variant[index])
            if (typeof entry === 'undefined') {
              entry = { value: variant[index], soldOut: true }
              options[index].push(entry)
            }

            const currentOption1 = currentlySelectedValues && currentlySelectedValues.find(({ index }) => index === 'option1')
            const currentOption2 = currentlySelectedValues && currentlySelectedValues.find(({ index }) => index === 'option2')

            switch (index) {
              case 'option1':
                entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut
                break
              case 'option2':
                if (currentOption1 && variant.option1 === currentOption1.value) {
                  entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut
                }
                break
              case 'option3':
                if (currentOption1 && variant.option1 === currentOption1.value && currentOption2 && variant.option2 === currentOption2.value) {
                  entry.soldOut = entry.soldOut && variant.available ? false : entry.soldOut
                }
            }
          })
          return options
        },
        { option1: [], option2: [], option3: [] }
      )
    }

    function toggleVariantOption(group, obj) {
      if (!group) return
      const value = escapeQuotes(obj.value)
      if (group.classList.contains('select-wrapper')) return

      const button = group.querySelector(`.dynamic-variant-button[data-option-value="${value}"]`)
      if (!button) return
      button.classList.remove('disabled')
      if (obj.soldOut) button.classList.add('disabled')
    }

    function disableVariantGroup(group) {
      if (!group) return
      if (group.classList.contains('select-wrapper')) return
      Array.from(group.querySelectorAll('.dynamic-variant-button')).forEach((button) => button.classList.add('disabled'))
    }

    function manageOptionState(option, values) {
      const group = container.querySelector(`.dynamic-variant-input-wrap[data-index="${option}"]`)
      values.forEach((obj) => toggleVariantOption(group, obj))
    }

    function setAvailability(selectedValues) {
      const valuesToManage = createAvailableOptionsTree(productVariants, selectedValues)
      Object.entries(valuesToManage).forEach(([option, values]) => manageOptionState(option, values))
    }

    function handleChange() {
      const currentlySelectedValues = variantSelectors.map((el) => ({ value: el.value, index: el.id }))
      setAvailability(currentlySelectedValues)
    }

    variantSelectorWrappers.forEach((group) => disableVariantGroup(group))

    const initiallySelectedVariant = productVariants.find((variant) => variant.id === parseInt(currentVariantId, 10))
    const currentlySelectedValues =
      initiallySelectedVariant && initiallySelectedVariant.options.map((value, index) => ({ value, index: `option${index + 1}` }))
    const initialOptions = createAvailableOptionsTree(productVariants, currentlySelectedValues)
    Object.entries(initialOptions).forEach(([option, values]) => manageOptionState(option, values))

    variantSelectors.forEach((el) => el.addEventListener('change', handleChange))

    return {
      unload() {
        variantSelectors.forEach((el) => el.removeEventListener('change', handleChange))
      },
    }
  }

  function initSiblingProducts(container) {
    const siblingProducts = container.querySelector('[data-sibling-products]')
    if (!siblingProducts) return null

    const siblingSwatches = Array.from(siblingProducts.querySelectorAll('[data-sibling-swatch]'))
    const labelValueEl = siblingProducts.querySelector('[data-sibling-label-value]')
    if (!siblingSwatches.length || !labelValueEl) return { unload() {} }

    const baseLabel = labelValueEl.innerText

    const handleOver = (e) => {
      labelValueEl.innerText = e.target.dataset.siblingCutline
    }
    const handleOut = () => {
      if (labelValueEl.innerText !== baseLabel) labelValueEl.innerText = baseLabel
    }

    siblingSwatches.forEach((item) => {
      item.addEventListener('mouseover', handleOver)
      item.addEventListener('mouseout', handleOut)
    })

    return {
      unload() {
        siblingSwatches.forEach((item) => {
          item.removeEventListener('mouseover', handleOver)
          item.removeEventListener('mouseout', handleOut)
        })
      },
    }
  }

  function initGiftCardRecipient(container) {
    const displayRecipientFormContainer = container.querySelector(
      ".product-form__gift-card-recipient[data-source='product-display']"
    )
    const formRecipientFormContainer = container.querySelector(".product-form__gift-card-recipient[data-source='product-form']")
    if (!displayRecipientFormContainer || !formRecipientFormContainer) return null

    const sectionID = displayRecipientFormContainer.dataset.sectionId

    const display = {
      controlInput: displayRecipientFormContainer.querySelector(`#display-gift-card-recipient-enable--${sectionID}`),
      emailInput: displayRecipientFormContainer.querySelector(`#display-gift-card-recipient-email--${sectionID}`),
      nameInput: displayRecipientFormContainer.querySelector(`#display-gift-card-recipient-name--${sectionID}`),
      messageInput: displayRecipientFormContainer.querySelector(`#display-gift-card-recipient-message--${sectionID}`),
      sendOnInput: displayRecipientFormContainer.querySelector(`#display-gift-card-recipient-send_on--${sectionID}`),
      recipientForm: displayRecipientFormContainer.querySelector('.gift-card-recipient-fields'),
      errors: Array.from(displayRecipientFormContainer.querySelectorAll('.product__gift-card-recipient-error')),
    }
    const form = {
      controlInput: formRecipientFormContainer.querySelector(`#form-gift-card-recipient-control--${sectionID}`),
      emailInput: formRecipientFormContainer.querySelector(`#form-gift-card-recipient-email--${sectionID}`),
      nameInput: formRecipientFormContainer.querySelector(`#form-gift-card-recipient-name--${sectionID}`),
      messageInput: formRecipientFormContainer.querySelector(`#form-gift-card-recipient-message--${sectionID}`),
      sendOnInput: formRecipientFormContainer.querySelector(`#form-gift-card-recipient-send_on--${sectionID}`),
      offsetInput: formRecipientFormContainer.querySelector(`#form-gift-card-recipient-timezone-offset--${sectionID}`),
    }

    // Pair each display input with its hidden form-submission counterpart, mirroring
    // the original `value.controls = elements.form[key]` wiring.
    const controlsMap = new Map([
      [display.controlInput, form.controlInput],
      [display.emailInput, form.emailInput],
      [display.nameInput, form.nameInput],
      [display.messageInput, form.messageInput],
      [display.sendOnInput, form.sendOnInput],
    ])

    function getDisplayInputs() {
      return [display.emailInput, display.nameInput, display.messageInput, display.sendOnInput].filter(Boolean)
    }
    function getFormInputs() {
      return [form.emailInput, form.nameInput, form.messageInput, form.sendOnInput].filter(Boolean)
    }
    function disableableInputs() {
      return [...getFormInputs(), form.controlInput, form.offsetInput].filter(Boolean)
    }
    function clearableInputs() {
      return [...getDisplayInputs(), ...getFormInputs()]
    }

    const handleChange = (e) => {
      const el = e.target
      const controls = controlsMap.get(el)
      if (controls) controls.value = el.value

      if (el.type === 'checkbox') {
        if (el.checked) {
          if (display.recipientForm) display.recipientForm.style.display = 'block'
        } else {
          clearableInputs().forEach((input) => (input.value = ''))
          display.errors.forEach((error) => error.classList.add('hidden'))
          if (display.recipientForm) display.recipientForm.style.display = 'none'
        }
        disableableInputs().forEach((input) => (input.disabled = !el.checked))
      }
    }

    formRecipientFormContainer.classList.add('visually-hidden')
    disableableInputs().forEach((input) => (input.disabled = true))
    if (form.offsetInput) form.offsetInput.value = new Date().getTimezoneOffset()

    const inputs = [display.controlInput, ...getDisplayInputs()].filter(Boolean)
    inputs.forEach((input) => input.addEventListener('change', handleChange))

    return {
      unload() {
        inputs.forEach((input) => input.removeEventListener('change', handleChange))
      },
    }
  }

  function initSocialShare(node) {
    if (!node) return () => {}
    const button = node.querySelector('.social-share__button')
    const popup = node.querySelector('.social-sharing__popup')
    const copyURLButton = node.querySelector('.social-share__copy-url')
    const successMessage = node.querySelector('.social-share__success-message')
    if (!button || !popup) return () => {}

    if (!navigator.clipboard || !navigator.clipboard.writeText) copyURLButton?.classList.add('hidden')

    function close() {
      button.setAttribute('aria-expanded', 'false')
      popup.setAttribute('aria-hidden', 'true')
    }
    function open() {
      button.setAttribute('aria-expanded', 'true')
      popup.setAttribute('aria-hidden', 'false')
    }
    function showMessage(message) {
      if (!successMessage) return
      successMessage.innerHTML = message
      successMessage.classList.remove('hidden')
      popup.classList.add('social-sharing__popup--success')
      setTimeout(() => {
        successMessage.classList.add('hidden')
        popup.classList.remove('social-sharing__popup--success')
      }, 2000)
    }

    function handleClick(evt) {
      const buttonClicked = evt.target.closest('.social-share__button') === button
      const popupClicked = evt.target.closest('.social-sharing__popup') === popup
      const copyURLClicked = copyURLButton && evt.target.closest('.social-share__copy-url') === copyURLButton

      let isActive = false
      if (buttonClicked) isActive = button.getAttribute('aria-expanded') === 'true'
      if (!popupClicked) close()
      if (buttonClicked && !isActive) open()

      if (copyURLClicked) {
        const { url } = copyURLButton.dataset
        navigator.clipboard
          .writeText(url)
          .then(() => showMessage(copyURLButton.dataset.successMessage))
          .catch(() => showMessage(copyURLButton.dataset.errorMessage || 'Error copying link.'))
      }
    }

    window.addEventListener('click', handleClick)
    return () => {
      close()
      window.removeEventListener('click', handleClick)
    }
  }

  /* -------------------------------------------------------------------------
   * Sticky ATC bar — frontend/scripts/lib/product/sticky-atc-bar.js
   * ---------------------------------------------------------------------- */

  function initStickyAtcBar(container) {
    const stickyAtcBar = container.querySelector('.sticky-atc-bar')
    if (!stickyAtcBar) return null

    const pageFooter = document.querySelector('footer')
    // Show sticky ATC as soon as the transactional buy button leaves the viewport
    // (not when the entire .product__primary block scrolls away).
    const buyButtonTrigger =
      container.querySelector('.product-form__controls-group--submit [data-add-to-cart]') ||
      container.querySelector('.product-form__controls-group--submit') ||
      container.querySelector('.product__primary') ||
      container
    const variantSelector = container.querySelector('.product__variants-wrapper')
    const subscription = container.querySelector('[data-product-subscription]')
    const price = stickyAtcBar.querySelector('[data-price-atc]')
    const purchaseType = stickyAtcBar.querySelector('[data-purchase-type]')
    const imageWrap = stickyAtcBar.querySelector('.product__media')
    const optionValues = stickyAtcBar.querySelector('[data-variant-options]')
    const changeOptionButton = stickyAtcBar.querySelector('[data-change-option-trigger]')

    const unsubscribes = []
    let widthWatcher

    if (changeOptionButton) {
      const clickHandler = () => variantSelector?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      changeOptionButton.addEventListener('click', clickHandler)
      unsubscribes.push(() => changeOptionButton.removeEventListener('click', clickHandler))
    }

    function setHeightVariable() {
      document.documentElement.style.setProperty('--sticky-atc-bar-height', `${stickyAtcBar.offsetHeight}px`)
    }
    function clearHeightVariable() {
      document.documentElement.style.setProperty('--sticky-atc-bar-height', '0px')
    }
    function showBar() {
      stickyAtcBar.classList.remove('is-hidden')
      setHeightVariable()
      let previousWidth = window.innerWidth
      widthWatcher = () => {
        const currentWidth = window.innerWidth
        if (currentWidth !== previousWidth) {
          previousWidth = currentWidth
          setHeightVariable()
        }
      }
      window.addEventListener('resize', widthWatcher)
    }
    function hideBar() {
      stickyAtcBar.classList.add('is-hidden')
      clearHeightVariable()
    }

    function updatePriceBySubscription() {
      if (!subscription || !purchaseType) return
      const checked = subscription.querySelector('input:checked')
      if (!checked) return

      const priceEl = checked.nextElementSibling?.querySelector('[data-price-trigger]')
      if (checked.value.includes('subscription')) {
        const frequencySelect = subscription.querySelector('.product-subscription__select')
        purchaseType.textContent =
          frequencySelect && frequencySelect.selectedOptions.length > 0
            ? frequencySelect.selectedOptions[0].textContent.trim()
            : 'Subscription'
      } else {
        purchaseType.textContent = 'One-time purchase'
      }

      if (priceEl && price) price.innerHTML = priceEl.dataset.priceTrigger
    }

    function switchCurrentImage(id) {
      if (imageWrap) switchImage(imageWrap, id)
    }

    function updateOptionValues(variant) {
      if (!optionValues) return
      let optionValueString = variant.options.join(', ')
      optionValueString = optionValueString.replace(/\s*[,/]\s*With dermaroller/i, '')
      optionValues.textContent = optionValueString
    }

    function syncFromCurrentSelection() {
      if (!optionValues) return
      const checkedInputs = Array.from(container.querySelectorAll('[name^="options"]:checked'))
      if (checkedInputs.length > 0) {
        let optionValueString = checkedInputs.map((input) => input.value).join(', ')
        optionValueString = optionValueString.replace(/\s*[,/]\s*With dermaroller/i, '')
        optionValues.textContent = optionValueString
      }
    }

    const buyButtonObserver = new IntersectionObserver(([entry]) => {
      updatePriceBySubscription()
      // Only show after the buy button has scrolled above the viewport —
      // not when it is still below the fold (not yet reached).
      if (entry.isIntersecting || entry.boundingClientRect.top > 0) hideBar()
      else showBar()
    })

    const footerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          buyButtonObserver.disconnect()
          hideBar()
        } else if (buyButtonTrigger) {
          buyButtonObserver.observe(buyButtonTrigger)
        }
      },
      { threshold: 0.8 }
    )

    if (pageFooter) footerObserver.observe(pageFooter)
    else if (buyButtonTrigger) buyButtonObserver.observe(buyButtonTrigger)

    if (subscription) {
      Array.from(subscription.querySelectorAll('input')).forEach((input) => {
        const handler = () => updatePriceBySubscription()
        input.addEventListener('change', handler)
        unsubscribes.push(() => input.removeEventListener('change', handler))
      })
      const frequencySelect = subscription.querySelector('.product-subscription__select')
      if (frequencySelect) {
        const handler = () => updatePriceBySubscription()
        frequencySelect.addEventListener('change', handler)
        unsubscribes.push(() => frequencySelect.removeEventListener('change', handler))
      }
    }

    const variantChangeHandler = (event) => {
      const { variant } = event.detail || {}
      if (variant) updateOptionValues(variant)
      updatePriceBySubscription()
    }
    document.addEventListener('product:variant-change', variantChangeHandler)
    unsubscribes.push(() => document.removeEventListener('product:variant-change', variantChangeHandler))

    function syncInitialState(variant) {
      if (variant?.options && optionValues) {
        let optionValueString = variant.options.join(', ')
        optionValueString = optionValueString.replace(/\s*[,/]\s*With dermaroller/i, '')
        optionValues.textContent = optionValueString
      } else {
        syncFromCurrentSelection()
      }
      updatePriceBySubscription()
      if (variant?.featured_media?.id) switchCurrentImage(variant.featured_media.id)
    }

    return {
      switchCurrentImage,
      updateOptionValues,
      syncInitialState,
      unload() {
        buyButtonObserver.disconnect()
        footerObserver.disconnect()
        unsubscribes.forEach((off) => off())
        window.removeEventListener('resize', widthWatcher)
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Sticky scroll — frontend/scripts/lib/sticky-scroll.js
   * ---------------------------------------------------------------------- */

  function initStickyScroll(node) {
    const stickyContainer = node.querySelector('[data-sticky-container]')
    if (!stickyContainer) return null

    // Accordion open/close (~220ms): freeze scroll-driven sticky updates and
    // pin content above the drawers via --sticky-container-top only (never
    // window.scrollTo — that was pushing users up/down the page).
    const HEIGHT_SCROLL_FREEZE_MS = 300
    const STICKY_SCROLL_MODE_EXIT_DELAY = 250
    const ANCHOR_LOCK_MS = 320

    node.style.setProperty('--sticky-container-top', '0')

    let previousScrollY = window.scrollY
    let headerHeight = 0
    let stickyFilterBarHeight = 0
    const stickyFilterBar = document.querySelector('.filter-bar--sticky')
    const root = document.documentElement
    root.style.setProperty('--sticky-filter-bar-height', '0px')

    let stickyContainerTop = headerHeight
    let stickyContainerTopPrevious = stickyContainerTop
    let stickyContainerHeight = stickyContainer.offsetHeight
    let stickyContainerHeightWithHeaderAndBar = stickyContainerHeight + headerHeight
    let stickyContainerMaxTop = stickyContainerHeightWithHeaderAndBar - window.innerHeight
    let stickyScrollModeTimer = null
    let freezeScrollUpdatesUntil = 0
    let anchorEl = null
    let anchorTop = 0
    let anchorTimer = null

    const stickyParent = stickyContainer.parentElement
    const stickyRow = node.querySelector('.product__primary, .collection__main-area, .search__main-area') || stickyParent?.parentElement
    const galleryMedia = node.querySelector(
      '.product__primary-left .product__media-container.above-mobile, .product__primary-left .js-product-media-desktop, .product__primary-left .product__media'
    )

    function getTopBounds() {
      const topMax = headerHeight + stickyFilterBarHeight
      const bottomMax = -stickyContainerMaxTop + headerHeight + stickyFilterBarHeight - 40
      return { topMax, bottomMax }
    }

    function setStickyTop(nextTop, immediate) {
      stickyContainerTop = nextTop
      if (immediate) {
        stickyContainerTopPrevious = nextTop
        node.style.setProperty('--sticky-container-top', `${nextTop}px`)
      }
    }

    function syncMetrics() {
      stickyContainerHeight = stickyContainer.offsetHeight
      if (stickyFilterBar) stickyFilterBarHeight = stickyFilterBar.offsetHeight

      headerHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--header-desktop-sticky-height').replace(/px/gi, '')
        ) || 0

      stickyContainerHeightWithHeaderAndBar = stickyContainerHeight + headerHeight + stickyFilterBarHeight
      stickyContainerMaxTop = stickyContainerHeightWithHeaderAndBar - window.innerHeight
    }

    // Viewport overflow only — do not exit when column outgrows the gallery
    // (that snap was shifting the buy button while mid sticky-scroll).
    function canUseStickyScroll() {
      return stickyContainerHeightWithHeaderAndBar > window.innerHeight
    }

    function enterStickyScrollMode() {
      if (stickyContainerTop === 0 && stickyContainerTopPrevious === 0 && headerHeight > 0) {
        setStickyTop(headerHeight, true)
      }
      node.classList.add('has-sticky-scroll')
    }

    function exitStickyScrollMode() {
      const { topMax } = getTopBounds()
      setStickyTop(topMax, true)
      node.classList.remove('has-sticky-scroll')
    }

    function lockStabilityAnchor(el) {
      if (!el || !stickyContainer.contains(el)) return
      anchorEl = el
      anchorTop = el.getBoundingClientRect().top
      if (anchorTimer) clearTimeout(anchorTimer)
      anchorTimer = setTimeout(() => {
        anchorEl = null
        anchorTimer = null
      }, ANCHOR_LOCK_MS)
    }

    // Keep content above the drawers (buy button) fixed in the viewport while
    // accordion height animates. Adjust sticky `top` only — never scroll the page.
    // At the bottom constraint, clamp may prevent full correction (expected).
    function correctStabilityAnchor() {
      if (!anchorEl || !document.contains(anchorEl)) return
      if (!node.classList.contains('has-sticky-scroll')) return

      const drift = anchorEl.getBoundingClientRect().top - anchorTop
      if (Math.abs(drift) < 0.5) return

      const { topMax, bottomMax } = getTopBounds()
      setStickyTop(Math.max(bottomMax, Math.min(stickyContainerTop - drift, topMax)), true)
    }

    function scrollHandler(y) {
      const currentScrollAmount = previousScrollY - y
      const currentScrollOffset = stickyContainerTop + currentScrollAmount
      const { topMax, bottomMax } = getTopBounds()
      stickyContainerTop = Math.max(bottomMax, Math.min(currentScrollOffset, topMax))
      previousScrollY = y
    }

    function observeHeight() {
      const wasStickyScroll = node.classList.contains('has-sticky-scroll')
      const previousHeight = stickyContainerHeight

      syncMetrics()

      const heightChanged = stickyContainerHeight !== previousHeight
      if (heightChanged) {
        freezeScrollUpdatesUntil = performance.now() + HEIGHT_SCROLL_FREEZE_MS
        previousScrollY = window.scrollY
      }

      const shouldStickyScroll = canUseStickyScroll()

      if (shouldStickyScroll) {
        if (stickyScrollModeTimer) {
          clearTimeout(stickyScrollModeTimer)
          stickyScrollModeTimer = null
        }

        if (!wasStickyScroll) {
          enterStickyScrollMode()
        }

        if (heightChanged) {
          correctStabilityAnchor()
        }
        return
      }

      if (wasStickyScroll) {
        if (heightChanged) {
          correctStabilityAnchor()
        }

        if (stickyScrollModeTimer) clearTimeout(stickyScrollModeTimer)
        stickyScrollModeTimer = setTimeout(() => {
          stickyScrollModeTimer = null
          syncMetrics()
          if (!canUseStickyScroll() && node.classList.contains('has-sticky-scroll')) {
            exitStickyScrollMode()
          }
        }, STICKY_SCROLL_MODE_EXIT_DELAY)
      }
    }

    function onAccordionPointerDown(event) {
      const summary = event.target.closest?.('summary')
      if (!summary || !stickyContainer.contains(summary)) return
      if (!summary.closest('details.accordion')) return

      // Pin the buy button (content above the drawers). Fallback to the clicked
      // summary if the ATC button isn't in this sticky column.
      const buyButton = stickyContainer.querySelector('[data-add-to-cart]')
      lockStabilityAnchor(buyButton || summary)
    }

    function scroller() {
      if (performance.now() < freezeScrollUpdatesUntil) {
        previousScrollY = window.scrollY
      } else {
        scrollHandler(window.scrollY)
      }

      if (stickyFilterBar) stickyFilterBarHeight = stickyFilterBar.offsetHeight
      root.style.setProperty('--sticky-filter-bar-height', `${stickyFilterBarHeight}px`)
    }

    window.addEventListener('scroll', scroller)
    window.addEventListener('resize', scroller)
    stickyContainer.addEventListener('pointerdown', onAccordionPointerDown, true)

    let resizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(observeHeight)
      resizeObserver.observe(stickyContainer)
      if (stickyParent) resizeObserver.observe(stickyParent)
      if (stickyRow) resizeObserver.observe(stickyRow)
      if (galleryMedia) resizeObserver.observe(galleryMedia)
      resizeObserver.observe(document.documentElement)
    }

    let rafId
    function updateLoop() {
      if (stickyContainerTop !== stickyContainerTopPrevious) {
        stickyContainerTopPrevious = lerp(stickyContainerTopPrevious, stickyContainerTop, 0.5)
        node.style.setProperty('--sticky-container-top', `${stickyContainerTopPrevious}px`)
      }
      rafId = requestAnimationFrame(updateLoop)
    }
    rafId = requestAnimationFrame(updateLoop)

    return {
      destroy() {
        window.removeEventListener('scroll', scroller)
        window.removeEventListener('resize', scroller)
        stickyContainer.removeEventListener('pointerdown', onAccordionPointerDown, true)
        resizeObserver?.disconnect()
        if (stickyScrollModeTimer) clearTimeout(stickyScrollModeTimer)
        if (anchorTimer) clearTimeout(anchorTimer)
        if (rafId) cancelAnimationFrame(rafId)
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Recently viewed products (localStorage) — frontend/scripts/lib/recently-viewed-products.js
   * ---------------------------------------------------------------------- */

  function updateRecentProducts(productHandle) {
    const ls = {
      get: () => {
        try {
          return JSON.parse(localStorage.getItem('fluco_sto_recentlyViewed'))
        } catch (e) {
          return null
        }
      },
      set: (val) => localStorage.setItem('fluco_sto_recentlyViewed', JSON.stringify(val)),
    }

    let recentlyViewed = []
    const existing = ls.get()
    if (existing !== null) {
      recentlyViewed = existing.filter((storedHandle) => storedHandle !== productHandle)
      recentlyViewed.unshift(productHandle)
      ls.set(recentlyViewed.slice(0, 16))
    } else {
      recentlyViewed.push(productHandle)
      ls.set(recentlyViewed)
    }
  }

  /* -------------------------------------------------------------------------
   * Store availability (pickup) — frontend/scripts/lib/store-availability.js
   * ---------------------------------------------------------------------- */

  function initStoreAvailability(container, product, variant) {
    function update(variant) {
      container.innerHTML = ''
      if (!variant) return
      const variantSectionUrl = `${container.dataset.baseUrl}/variants/${variant.id}/?section_id=store-availability`

      makeRequest(variantSectionUrl).then((html) => {
        if (html.trim() === '') return
        const wrapper = document.createElement('div')
        wrapper.innerHTML = html.trim()
        container.innerHTML = wrapper.firstElementChild ? wrapper.firstElementChild.innerHTML : ''
        container.setAttribute('data-variant-id', variant.id)
        container.setAttribute('data-product-title', product.title)
        container.setAttribute('data-variant-title', variant.public_title || '')
      })
    }

    update(variant)

    return {
      update,
      unload() {
        container.innerHTML = ''
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Native scroll-snap carousel (replaces Swiper for the mobile "featured
   * products" block and any other simple horizontal slide-lists here)
   * ---------------------------------------------------------------------- */

  function initNativeCarousel(node, options = {}) {
    const viewport = node.classList.contains('swiper') ? node : node.querySelector('.swiper')
    if (!viewport) return null

    const track = viewport.querySelector('.swiper-wrapper') || viewport
    track.classList.add('flex', 'snap-x', 'snap-mandatory', 'overflow-x-auto', 'scroll-smooth')
    viewport.classList.add('overflow-hidden')

    const slides = Array.from(track.children)
    slides.forEach((slide) => slide.classList.add('snap-start', 'shrink-0'))

    if (options.slidesPerView) {
      const percent = 100 / options.slidesPerView
      slides.forEach((slide) => {
        slide.style.width = `${percent}%`
      })
    }
    if (options.spaceBetween) {
      track.style.gap = `${options.spaceBetween}px`
    }

    const nextButton = node.querySelector('[data-next]')
    const prevButton = node.querySelector('[data-prev]')

    function scrollByAmount(direction) {
      const amount = viewport.clientWidth * 0.8 * direction
      track.scrollBy({ left: amount, behavior: 'smooth' })
    }

    const nextHandler = () => scrollByAmount(1)
    const prevHandler = () => scrollByAmount(-1)
    nextButton?.addEventListener('click', nextHandler)
    prevButton?.addEventListener('click', prevHandler)

    function updateOverflow() {
      const hasOverflow = track.scrollWidth > track.clientWidth + 1
      viewport.classList.toggle('has-overflow', hasOverflow)
    }
    updateOverflow()
    window.addEventListener('resize', updateOverflow)

    return {
      destroy() {
        nextButton?.removeEventListener('click', nextHandler)
        prevButton?.removeEventListener('click', prevHandler)
        window.removeEventListener('resize', updateOverflow)
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Featured products block (PDP recommendations) — frontend/scripts/lib/product/featured-products.js
   * ---------------------------------------------------------------------- */

  function initFeaturedProducts(node) {
    const featuredProductsEls = Array.from(node.querySelectorAll('[data-featured-products]'))
    if (!featuredProductsEls.length) return null

    let productItemInstances = []
    let mobileCarousel = null

    const { recommendationsType, productId: id, sectionId, enableMobileSwiper, maxRecommendations } =
      featuredProductsEls[0].dataset

    function initProductItems() {
      productItemInstances = featuredProductsEls
        .map((productContainer) =>
          window.OneskinProductItem ? window.OneskinProductItem(productContainer) : null
        )
        .filter(Boolean)
    }

    function handleMobileSwiper() {
      if (enableMobileSwiper !== 'true') return
      const mobileFeaturedProducts =
        node.querySelector('.left-side-blocks.for-mobile [data-featured-products]') || featuredProductsEls[0]

      function maybeInit() {
        if (window.matchMedia(getMediaQuery('below-720')).matches) {
          mobileCarousel = initNativeCarousel(mobileFeaturedProducts.closest('.carousel') || mobileFeaturedProducts, {
            slidesPerView: 2.1,
            spaceBetween: 12,
          })
        } else {
          mobileCarousel?.destroy()
        }
      }
      maybeInit()
      watchBreakpoint(720, maybeInit)
    }

    if (recommendationsType === 'app-recommendations') {
      const requestUrl = `${theme.routes?.productRecommendations || '/recommendations/products'}?section_id=${sectionId}&limit=${maxRecommendations}&product_id=${id}&intent=complementary`

      fetch(requestUrl)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div')
          html.innerHTML = text
          const recommendations = html.querySelector('[data-featured-products-content]')

          if (recommendations && recommendations.innerHTML.trim().length) {
            featuredProductsEls.forEach((block) => (block.innerHTML = recommendations.innerHTML))
            initProductItems()
            featuredProductsEls.forEach((block) => {
              block.closest('.product__block-featured-products')?.classList.remove('hidden')
            })
            handleMobileSwiper()
          }
        })
        .catch((error) => console.error('[product] featured products fetch failed', error))
    } else {
      initProductItems()
      handleMobileSwiper()
    }

    return {
      unload() {
        productItemInstances.forEach((item) => item.unload?.())
        mobileCarousel?.destroy()
      },
    }
  }

  /* -------------------------------------------------------------------------
   * Product-level reveal animation (frontend/scripts/lib/animation/product.js)
   * ---------------------------------------------------------------------- */

  function initProductAnimation(node) {
    if (!shouldAnimate(node)) return { destroy() {} }
    delayOffset(node, ['.animation--product-media'])
    const observer = intersectionReveal(node, true)
    return { destroy: () => observer.destroy() }
  }

  /* -------------------------------------------------------------------------
   * Mobile description "show more/less" truncation — frontend/scripts/templates/product.js
   * ---------------------------------------------------------------------- */

  function initTruncateDescriptions(container) {
    const truncateWrappers = Array.from(container.querySelectorAll('.truncate-on-mobile'))
    if (truncateWrappers.length === 0) return () => {}

    function truncateText(wrapper) {
      const rte = wrapper.querySelector('.rte')
      const button = wrapper.querySelector('.show-more-toggle')
      if (!rte || !button) return

      if (!rte.dataset.originalContent) rte.dataset.originalContent = rte.innerHTML
      if (!rte.dataset.originalShowMoreText) rte.dataset.originalShowMoreText = button.textContent || button.innerText

      const showMoreText = rte.dataset.originalShowMoreText
      const showLessText = button.getAttribute('data-show-less-text') || 'Show less'
      const originalContent = rte.dataset.originalContent

      if (window.innerWidth >= 720) {
        rte.innerHTML = originalContent
        button.style.display = 'none'
        wrapper.classList.remove('js-no-overflow')
        wrapper.classList.add('js-truncated')
        wrapper.classList.remove('is-expanded')
        return
      }

      if (wrapper.classList.contains('is-expanded')) {
        rte.innerHTML = `${originalContent} <button class="show-more-toggle inline" style="margin-bottom: 12px;" type="button">${showLessText}</button>`
        wrapper.classList.add('js-truncated')
        const p = rte.querySelector('p')
        if (p) p.style.marginBottom = '4px'

        const newButton = rte.querySelector('.show-more-toggle')
        if (newButton) {
          newButton.addEventListener('click', () => {
            wrapper.classList.remove('is-expanded')
            truncateText(wrapper)
          })
        }
        return
      }

      rte.innerHTML = originalContent

      const oneLineHeight = (() => {
        const oneLineElement = rte.cloneNode(true)
        oneLineElement.style.position = 'absolute'
        oneLineElement.style.visibility = 'hidden'
        oneLineElement.style.height = 'auto'
        oneLineElement.style.webkitLineClamp = 'unset'
        oneLineElement.innerHTML = 'Test line'
        rte.parentNode.appendChild(oneLineElement)
        const measured = oneLineElement.offsetHeight
        rte.parentNode.removeChild(oneLineElement)
        return measured
      })()
      const twoLinesHeight = oneLineHeight * 2

      if (rte.scrollHeight <= twoLinesHeight) {
        wrapper.classList.add('js-no-overflow')
        wrapper.classList.add('js-truncated')
        return
      }
      wrapper.classList.remove('js-no-overflow')

      const words = originalContent.split(' ')
      let low = 0
      let high = words.length
      let bestFit = 0

      while (low <= high) {
        const mid = Math.floor((low + high) / 2)
        const truncatedText = words.slice(0, mid).join(' ')
        const testContent = mid === 0 ? '' : `${truncatedText}... `
        rte.innerHTML = `${testContent}<button class="show-more-toggle inline" type="button">${showMoreText}</button>`

        if (rte.scrollHeight <= twoLinesHeight) {
          bestFit = mid
          low = mid + 1
        } else {
          high = mid - 1
        }
      }

      let truncatedContent
      if (bestFit === 0) {
        truncatedContent = `<button class="show-more-toggle inline" type="button">${showMoreText}</button>`
      } else {
        const finalText = words.slice(0, bestFit).join(' ')
        truncatedContent = `${finalText}... <button class="show-more-toggle inline" type="button">${showMoreText}</button>`
      }
      rte.innerHTML = truncatedContent
      wrapper.classList.add('js-truncated')

      const newButton = rte.querySelector('.show-more-toggle')
      if (newButton) {
        newButton.addEventListener('click', () => {
          wrapper.classList.add('is-expanded')
          truncateText(wrapper)
        })
      }
    }

    truncateWrappers.forEach((wrapper) => truncateText(wrapper))

    let resizeTimeout
    const onResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => truncateWrappers.forEach((wrapper) => truncateText(wrapper)), 100)
    }
    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }

  function isElementShown(element) {
    if (!(element instanceof Element)) return false
    if (typeof element.checkVisibility === 'function') {
      return element.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true })
    }
    return element.getClientRects().length > 0
  }

  function warmLazyProductImages(container) {
    if (navigator.connection && navigator.connection.saveData) return

    const roots = [...container.querySelectorAll('[data-warmup-lazy-images]')].filter(isElementShown)
    if (!roots.length) return

    roots.forEach((root) => {
      root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        const item = img.closest('[data-image-visible]')
        if (item && item.getAttribute('data-image-visible') === 'false') return
        if (img.complete && img.naturalWidth > 0) return
        img.fetchPriority = 'low'
        img.loading = 'eager'
      })
    })
  }

  function scheduleWarmLazyProductImages(container) {
    const run = () => warmLazyProductImages(container)
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 1500 })
    } else {
      window.setTimeout(run, 1)
    }
  }

  /* -------------------------------------------------------------------------
   * ProductSection — orchestrator (frontend/scripts/lib/product/product.js)
   * ---------------------------------------------------------------------- */

  class ProductSection {
    constructor(node) {
      this.container = node

      const {
        isQuickView,
        isFullProduct,
        isFeaturedProduct,
        enableStickyContainer,
        loopMobileCarousel,
        hideMobileCarouselDots,
        enableMultipleVariantMedia,
        initialMediaId,
        sectionId,
        productHandle,
      } = this.container.dataset

      this.isQuickView = isQuickView
      this.isFullProduct = isFullProduct
      this.isFeaturedProduct = isFeaturedProduct
      this.loopMobileCarousel = loopMobileCarousel === 'true'
      this.hideMobileCarouselDots = hideMobileCarouselDots === 'true'
      this.enableMultipleVariantMedia = enableMultipleVariantMedia
      this.sectionId = sectionId
      this.productHandle = productHandle
      this.listeners = []

      this.formElement = this.container.querySelector('[data-product-form]')
      this.quantityError = this.container.querySelector('[data-quantity-error]')
      this.variantsWrapper = this.container.querySelector('[data-current-variant-id]')
      this.displayedDiscount = this.container.querySelector('[data-discount-display]')
      this.viewInYourSpace = this.container.querySelector('[data-in-your-space]')
      this.productSubscriptionSelect = this.container.querySelector('[data-product-subscription-select]')

      this._initVariantUrl()

      this.mediaManager = createMediaManager({
        container: this.container,
        sectionId: this.sectionId,
        isFullProduct: this.isFullProduct,
        isFeaturedProduct: this.isFeaturedProduct,
        enableMultipleVariantMedia: this.enableMultipleVariantMedia,
        initialMediaId,
        viewInYourSpace: this.viewInYourSpace,
      })

      this._carouselUpdateScheduled = false
      this.breakPointOff = watchBreakpoint(960, () => {
        if (this.isFullProduct && this.container.dataset.aeroGallery === 'true') {
          this.scheduleVariantMediaUpdate({ variantId: this.getCurrentVariant()?.id })
        }
        this._initStickyScroll()
      })

      this.mediaManager.initThumbnails()

      this.storeAvailabilityContainer = this.container.querySelector('[data-store-availability-container]')
      this.availability = null

      if (this.isFullProduct) {
        this._variantMediaUpdateHandler = (event) => this.scheduleVariantMediaUpdate(event.detail || {})
        document.addEventListener('variant:media-update', this._variantMediaUpdateHandler)
      }

      if (this.formElement) this._initProductForm(initialMediaId)

      this._initComponents(enableStickyContainer)

      this.mediaManager.loadMedia()
      repositionMediaBadges(this.container, { featuredMediaId: initialMediaId })
      this._initEvents()

      this.variantAvailability = initVariantAvailability(this.container)
      this.siblingProducts = initSiblingProducts(this.container)
      this.giftCardRecipient = initGiftCardRecipient(this.container)
      this.stickyAtcBar = initStickyAtcBar(this.container)

      if (this.stickyAtcBar) {
        const initialVariant = this.getCurrentVariant()
        this.stickyAtcBar.syncInitialState(initialVariant)
      }

      this.productAnimation = initProductAnimation(this.container)
      scheduleWarmLazyProductImages(this.container)
    }

    _initVariantUrl() {
      const urlParams = new URLSearchParams(window.location.search)
      const variantId = urlParams.get('variant')
      if (!variantId && this.variantsWrapper) {
        const defaultVariantId = this.variantsWrapper.dataset.currentVariantId
        if (defaultVariantId) {
          const url = getUrlWithVariant(window.location.href, defaultVariantId)
          window.history.replaceState({ path: url }, '', url)
        }
      }
    }

    _initProductForm(initialMediaId) {
      const { currentProductId } = this.formElement.dataset
      const $productJson = this.container.querySelector('[id^="ProductJson-"]')
      if (!$productJson) {
        console.error('[product] Product JSON not found')
        return
      }

      this.productData = JSON.parse($productJson.textContent)
      window.productData = this.productData

      const variant = getVariantFromId(this.productData, parseInt(currentProductId))

      if (this.storeAvailabilityContainer && variant) {
        this.availability = initStoreAvailability(this.storeAvailabilityContainer, this.productData, variant)
      }

      this.variantHandler = createVariantHandler({
        container: this.container,
        formElement: this.formElement,
        isFullProduct: this.isFullProduct,
      })

      this.subscriptionHandler = createSubscriptionHandler({
        container: this.container,
        formElement: this.formElement,
        productData: this.productData,
      })

      this.cartHandler = createCartHandler({
        container: this.container,
        formElement: this.formElement,
        quantityError: this.quantityError,
        getCurrentVariant: () => this.getCurrentVariant(),
      })

      this.productForm = ProductFormController(this.container, this.formElement, this.productData)

      this._initProductFormEvents()
      this._syncInitialVariantState(variant)

      if (this.subscriptionHandler) {
        this.subscriptionEvents = this.subscriptionHandler.initSubscriptionListeners()
      } else {
        Array.from(this.container.querySelectorAll('[data-add-to-cart]')).forEach((button) =>
          button.classList.remove('loading')
        )
      }

      if (this.mediaManager.productThumbnails && variant?.featured_media?.id == initialMediaId) {
        this.mediaManager.scrollThumbnails(variant)
      }

      this._initInventoryCounter(variant)
    }

    _initInventoryCounter(variant) {
      const productInventoryJson = this.container.querySelector('[data-product-inventory-json]')
      if (productInventoryJson && variant) {
        const jsonData = JSON.parse(productInventoryJson.innerHTML)
        const variantsInventories = jsonData.inventory
        if (variantsInventories) {
          this.inventoryCounter = initInventoryCounter(this.container, { id: variant.id, variantsInventories })
        }
      }
    }

    _initComponents(enableStickyContainer) {
      this.quantityInput = initQuantityInput(this.container)
      this.customOptionInputs = Array.from(this.container.querySelectorAll('[data-custom-option-input]'))
      this.socialButtons = Array.from(this.container.querySelectorAll('[data-social-share]'))
      this.featuredProducts = initFeaturedProducts(this.container)

      if (enableStickyContainer === 'true') this._initStickyScroll()

      const optionButtonGroups = Array.from(this.container.querySelectorAll('[data-option-buttons]'))
      this.optionButtons = optionButtonGroups.length ? initOptionButtons(optionButtonGroups) : null
      this.informationPopup = initInformationPopup(this.container)

      const productDescriptionWrapper = this.container.querySelector('.product__description')
      if (productDescriptionWrapper) {
        wrapIframes(Array.from(productDescriptionWrapper.querySelectorAll('iframe')))
        wrapTables(Array.from(productDescriptionWrapper.querySelectorAll('table')))
      }

      const socialShareContainer = this.container.querySelector('.social-share')
      if (socialShareContainer) this.socialShareDestroy = initSocialShare(socialShareContainer)

      if (this.isFullProduct && this.productHandle !== null && this.productHandle !== undefined) {
        updateRecentProducts(this.productHandle)
      }

      this._stopTruncate = initTruncateDescriptions(this.container)
    }

    _initProductFormEvents() {
      this._boundOptionChange = this.onOptionChange.bind(this)
      this._boundQuantityChange = this.onQuantityChange.bind(this)
      this._boundFormSubmit = this.onFormSubmit.bind(this)

      this.container.addEventListener('product-form:option-change', this._boundOptionChange)
      this.container.addEventListener('product-form:quantity-change', this._boundQuantityChange)
      this.container.addEventListener('product-form:submit', this._boundFormSubmit)

      this.listeners.push(
        () => this.container.removeEventListener('product-form:option-change', this._boundOptionChange),
        () => this.container.removeEventListener('product-form:quantity-change', this._boundQuantityChange),
        () => this.container.removeEventListener('product-form:submit', this._boundFormSubmit)
      )
    }

    _syncInitialVariantState(initialVariant) {
      if (!initialVariant) {
        console.error('[product] Missing initial variant during product initialization')
        return
      }
      this.onOptionChange({ detail: { formState: { variant: initialVariant }, srcElement: null } })
    }

    _initEvents() {
      const thumbnailClickHandler = (e) => {
        e.preventDefault()
        const { dataset } = e.currentTarget
        this.mediaManager.productThumbnailItems.forEach((thumb) => thumb.classList.remove('active'))
        e.currentTarget.classList.add('active')
        switchImage(this.mediaManager.desktopMedia, dataset.thumbnailId, this.viewInYourSpace)
      }

      this.mediaManager.productThumbnailItems.forEach((thumb) => {
        thumb.addEventListener('click', thumbnailClickHandler)
        this.listeners.push(() => thumb.removeEventListener('click', thumbnailClickHandler))
      })

      if (this.customOptionInputs) {
        this.customOptionInputs.forEach((input) => {
          const id = input.dataset.customOptionInput
          const target = this.container.querySelector(`[data-custom-option-target='${id}']`)
          if (!target) return

          const changeHandler = (e) => {
            if (e.target.type === 'checkbox') target.checked = e.target.checked
            else target.value = e.target.value
          }
          input.addEventListener('change', changeHandler)
          this.listeners.push(() => input.removeEventListener('change', changeHandler))
        })
      }

      this.mediaManager.setEvents(this.listeners)
    }

    _initStickyScroll() {
      const mobileQuery = window.matchMedia(getMediaQuery('below-960'))
      if (mobileQuery.matches) {
        if (this.stickyScroll) {
          this.stickyScroll.destroy()
          this.stickyScroll = null
        }
      } else if (!this.stickyScroll) {
        this.stickyScroll = initStickyScroll(this.container)
      }
    }

    onOptionChange(event) {
      const { formState, srcElement } = event.detail
      const { variant } = formState

      if (srcElement) this.variantHandler.updateOptionLabel(srcElement)
      this.variantHandler.updatePriceDisplay(variant)

      const defaultProductTemplate = this.isFullProduct === 'true'
      updatePrices(this.container, variant, defaultProductTemplate)

      if (this.subscriptionHandler) {
        this.subscriptionHandler.updateBuyButtonWithSubscription()
      } else if (variant) {
        const hasSellingPlan = variant.selling_plan_allocations?.length > 0
        const subscriptionPrice = hasSellingPlan ? variant.selling_plan_allocations[0]?.price : null
        this.variantHandler.updateBuyButtons(variant, {
          isSubscription: hasSellingPlan,
          oneTimePrice: variant.price,
          subscriptionPrice,
          compareAtPrice: variant.compare_at_price,
        })
      }

      updateUnitPrices(this.container, variant)
      updateSku(this.container, variant)
      this.availability?.update(variant)
      this.variantHandler.updateDiscountDisplay(variant)
      this.inventoryCounter?.update(variant)
      this.variantHandler.dispatchVariantChangeEvent(variant)

      if (!variant) {
        this.variantHandler.handleUnavailableVariant()
        this.availability?.unload()
        return
      }

      this.variantHandler.updateVariantUrl(variant)
      this.variantHandler.updateVariantSelect(variant)

      if (variant.featured_media) {
        const result = this.mediaManager.updateForVariant(variant)
        if (result?.shouldRefresh) {
          this.mediaManager.refreshOverviewWithVariant(variant.id, {
            reloadEvents: () => this._initEvents(),
          })
        }
      }

      if (this.stickyAtcBar) {
        if (variant.featured_media) this.stickyAtcBar.switchCurrentImage(variant.featured_media.id)
        this.stickyAtcBar.updateOptionValues(variant)
      }
    }

    onQuantityChange(event) {
      const { formState } = event.detail
      const { variant, quantity } = formState

      Array.from(this.formElement.querySelectorAll('[name="quantity"]')).forEach((input) => {
        input.value = quantity
      })

      dispatchGlobalEvent('product:quantity-update', { quantity, variant })
    }

    onFormSubmit(event) {
      this.cartHandler.handleFormSubmit(event)
    }

    getCurrentVariant() {
      const variantInput = this.formElement?.querySelector('[name="id"]')
      const variantId = variantInput?.value

      if (variantId && this.productData?.variants) {
        return this.productData.variants.find((v) => v.id.toString() === variantId) || null
      }
      if (this.productForm) {
        try {
          return this.productForm.getVariant()
        } catch (e) {
          return null
        }
      }
      return null
    }

    highlightActiveThumbnail(photos, variant) {
      this.mediaManager.highlightActiveThumbnail(photos, variant)
    }

    scrollThumbnails(variant) {
      this.mediaManager.scrollThumbnails(variant)
    }

    scheduleVariantMediaUpdate(detail = {}) {
      this._pendingCarouselDetail = { ...(this._pendingCarouselDetail || {}), ...detail }
      if (this._carouselUpdateScheduled) return

      this._carouselUpdateScheduled = true
      requestAnimationFrame(() => {
        this._carouselUpdateScheduled = false
        const pendingDetail = this._pendingCarouselDetail || {}
        this._pendingCarouselDetail = null

        const variantContext = this.mediaManager.syncVariantMediaFilters(pendingDetail)
        if (!variantContext) return

        if (this.container.dataset.aeroGallery === 'true') {
          this.mediaManager.updateMobileCarouselOnly(variantContext)
        }
      })
    }

    unload() {
      this.mediaManager.unloadMedia()
      this.quantityInput?.unload()
      this.listeners.forEach((unsubscribe) => unsubscribe())
      this.subscriptionEvents?.forEach((unsubscribe) => unsubscribe())
      this.stickyScroll?.destroy()
      this.featuredProducts?.unload()
      this.variantAvailability?.unload()
      this.siblingProducts?.unload()
      this.giftCardRecipient?.unload()
      this.stickyAtcBar?.unload()
      this.optionButtons?.destroy()
      this.informationPopup?.unload()
      this.socialShareDestroy?.()
      this.productForm?.destroy()
      this.productAnimation?.destroy()
      this.breakPointOff?.()
      this._stopTruncate?.()
      if (this._variantMediaUpdateHandler) {
        document.removeEventListener('variant:media-update', this._variantMediaUpdateHandler)
      }
    }
  }

  window.OneskinProduct = ProductSection

  /* -------------------------------------------------------------------------
   * Auto-init — matches [data-section-type="product"] (default PDP + quick view)
   * and [data-section-type="featured-product"] (featured-product section).
   * ---------------------------------------------------------------------- */

  function autoInit() {
    document
      .querySelectorAll('[data-section-type="product"]:not([data-product-initialized]), [data-section-type="featured-product"]:not([data-product-initialized])')
      .forEach((container) => {
        container.setAttribute('data-product-initialized', 'true')
        try {
          // eslint-disable-next-line no-new
          new ProductSection(container)
        } catch (err) {
          console.error('[product] Failed to initialize product section', err)
        }
      })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit)
  } else {
    autoInit()
  }

  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('[data-section-type="product"], [data-section-type="featured-product"]')
    if (container && !container.hasAttribute('data-product-initialized')) {
      container.setAttribute('data-product-initialized', 'true')
      new ProductSection(container)
    }
  })
})()
